'use strict';

const ContentUtils = require('utils/ContentUtils'),
    NumberUtils = require('utils/NumberUtils'),
    OrderUtils = require('utils/OrderUtils'),
    DateUtils = require('utils/DateUtils'),
    {map, filter, includes, find, some, sortBy, reduce, groupBy, every, orderBy} = require('lodash/collection'),
    {cloneDeep} = require('lodash/lang'),
    {uniq, uniqBy} = require('lodash/array'),
    {flatMap} = require('lodash/collection'),
    {assign, merge, values} = require('lodash/object'),
    get = require('lodash/object').get,
    set = require('lodash/object').set,
    DashboardModalUtils = require('components/DashboardModal/DashboardModalUtils'),
    Constants = require('constants/AppConstants'),
    {concat, union, flatten} = require('lodash/array'),
    SessionStore = require('stores/SessionStore'),
    DashboardServiceCache = require('services/DashboardServiceCache'),
    InventoryServiceImpl = require('services/InventoryServiceImpl');


/**
 *
 * <h1> Products integration with dashboard </h1>
 *
 * <h2> NEW ORDER FLOW </h2>
 *
 * <ol>
 *     <li> First of all, when modal is loaded don't care about products. Because the beers style is not selected yet. </li>
 *
 * <li> Once the beer style are selected, if the beer style have any icon configuration that we need to load
 * products then we need to load in the state context the product which need to be consume for the order.
 * (`state.products` must be loaded which all icons that consume the order) </li>
 *
 * <li> When selected the icon (in some section), we need check if need to hydrated the task with products
 * (depend of the icon and beer style already selected) </li>
 *
 * <li>If defined some product amount on some product in some task, the product must be added to the contexts
 * of the order (state.products), in order to be able after when click submit those products. </li>
 *
 * <h2> CHANGE ORDER FLOW </h2>
 *
 * <li> We need to check which products are already assigned to the order and then hydrate each of one. </li>
 *
 * <li> When editing the order, the default configuration is not used (default beer styles only apply to new orders). </li>
 *
 */
const InventoryStore = {
    
    /**
     * Locate the place of each location available item & purchase item by each items
     * of each product of each task by each order and estimate where will have each
     * items in chronologically way.
     *
     * @param {Array} orders
     * @param {String} startingScopeInventoryDate (Optional)                 given date will determine the starting scope of the scope inventory
     * @param {String} endingScopeInventoryDate (Optional)            given date will determine the ending scope of the scope inventory
     */
    hydrateOrderTaskWithInventoryScopeEstimation(
      orders,
      startingScopeInventoryDate = SessionStore.getMaxScopeInventoryDate(),
      endingScopeInventoryDate
    ) {
        if (!SessionStore.isFullPaidVersion() || !SessionStore.isCalculatingInventory()) {
            return orders;
        }
        
        const locationItemsAvailable = DashboardServiceCache.getLocationItems();
        const purchaseItems = DashboardServiceCache.getVendorPurchaseItems();
        const allSortedTasks = OrderUtils.getAllSortedTasksFromOrders(orders);
        const filteredSortedTasks = this._filterScopeInventory(allSortedTasks, startingScopeInventoryDate, endingScopeInventoryDate);
        
        this._hydrateInventoryScopeEstimationByTasks(filteredSortedTasks, locationItemsAvailable, purchaseItems);
        
        return orders;
    },
    
    _hydrateInventoryScopeEstimationByTasks(
      tasks,
      locationItemsAvailable,
      purchaseItems
    ) {
        const allItemsIdNeeded = OrderUtils.getAllItemsIdFromTasks(tasks);
        const purchaseItemRequested = this._filterPurchaseOrderOrdered(purchaseItems, allItemsIdNeeded);
        locationItemsAvailable = this._discountConfirmedProductFromLocationItemAvailable(tasks, locationItemsAvailable, allItemsIdNeeded);
        
        for (let task of tasks) {
            task = this._hydrateOrderTaskWithInventoryScopeEstimationByTask(task, locationItemsAvailable, purchaseItemRequested);
        }
    },
    
    /**
     * We need to discounted the items from confirmed product first, otherwise we will end in the bug related to
     * confirmed orders which reserve an inventory amount will not be reserve that amount in case that you create a
     * order before. So, to prevent that bug, we should discount all confirmed product before checking inventory task
     * in a chronological way.
     * @param locationItemsAvailable
     * @param allItemsIdNeeded
     * @returns {Array}
     * @private
     */
    _discountConfirmedProductFromLocationItemAvailable(tasks, locationItemsAvailable, allItemsIdNeeded) {
        const locationItemsAvailableOnlyNeeded = this._filterLocationItemNeeded(tasks, locationItemsAvailable, allItemsIdNeeded);
        const confirmedProducts = OrderUtils.getAllProductsFromTasks(tasks).filter(product => product.isConfirmed && !product.isDiscounted);
        return this.updateLocationItemsWithProducts(confirmedProducts, locationItemsAvailableOnlyNeeded);
    },
    
    /**
     * Calculate if have enough items available and purchase item available to
     * archive the inventory needed by the current future (after to day) orders.
     *
     * NOTE: this method mutate the original reference of given Task, adding
     * flags to indicate is location items & purchase items are enough. Also
     * mutate given locationItemsAvailable in order to discount those product which are already confirmed.
     * (in this case, when the user confirm the product we need to simulate at dashboard level as all of the item of the
     * product was discounted, so it's more like a logical discount not physical, until the publishing of changes will be executed).
     *
     * @param {Object} task                     The task which will be analise the current scope inventory.
     * @param {Array} locationItemsAvailable    The current location items available in the application.
     * @param {Array} purchaseItems             The current purchase items (with state `ORDERED`) in the application.
     * @return {Object} task                    Return the original task which additional flags about scope inventory
     */
    _hydrateOrderTaskWithInventoryScopeEstimationByTask(
      task,
      locationItemsAvailable,
      purchaseItems
    ) {
        
        // Add an item & product level info about if
        // is enough or not the existing inventory or purchased orders
        task.products = map(uniqBy(task.products, 'id'), product => {
    
            if (product.isConfirmed || product.isNeededCalculateInventory === false) {
                // enter here means the product is planned or already discounted,
                // therefore do nothing with it
                
                //  The `isNeededCalculateInventory` it's needed to don't reset the inventory when you
                // are for example in the modal, and you are changing WhereInventortWasTaken (of one item in particular)
                return product;
            }
            
            const haveAmountNeeded = NumberUtils.isGreater(product.amount, 0);
            if (!haveAmountNeeded) {
                // enter here means the product don't already have
                // the `amount` defined by the client.
                
                // clean items estimation in case that exist one before
                product = _resetItemsEstimation(product);
                return product;
            }
            
            if (product.needToPublishDisconfirmation) {
                // enter here means the order was discounted, but was unconfirmed
                // so we need simulate like we already return the item to the inventory
                // (again it's still logically not physically, until the customer perform the publishing operation)
                locationItemsAvailable = this.returnItemsConsumed(product, locationItemsAvailable);
            }
            
            const taskDate = get(task, 'date');
    
            // reset/initialize product in order to calculate properly with fresh state.
            product = this._resetInventoryFromProduct(product);
            
            // process each item of the product
            product.items = map(product.items, productItem => {
                const itemNotNeedSubtract = productItem.addition === false && ContentUtils.isNullOrEmpty(productItem.whereInventoryWasTakenSave);
                
                if (!itemNotNeedSubtract) {
                    return productItem;
                }
                
                productItem = this.processItemNonDiscountedYet(
                  productItem,
                  locationItemsAvailable,
                  taskDate,
                  purchaseItems
                );
                
                if (productItem.isEnoughInventory === false) {
                    product.someItemsIsNotEnoughInStock = true;
                }
                
                if (productItem.isEnoughInventory === false && productItem.isPurchasedAmountEnough === false) {
                    product.someItemsIsNotEnoughInPurchase = true;
                }
                
                return productItem;
            });
            
            return product;
        });
        
        // this mark says about if the current availability stock
        // is enough to perform all items of all product for this date
        task.someItemsIsNotEnoughInStock = some(task.products, product => product.someItemsIsNotEnoughInStock);
        
        // this mark says that some items is not enough in purchased items
        task.someItemsIsNotEnoughInPurchase = some(task.products, product => product.someItemsIsNotEnoughInPurchase);
        
        // this mark says if exist at least one product confirmed
        task.someItemsWithConfirmedProduct = some(task.products, product => product.isConfirmed);
        
        return task;
    },
    
    _resetInventoryFromProduct(product) {
        product.someItemsIsNotEnoughInStock = false;
        product.someItemsIsNotEnoughInPurchase = false;
        product.items = map(product.items, item => {
            item.whereInventoryWasTakenNew = []; // reset the state of item changed from UI not persisted yet
            item.existItemInStock = false;
            item.isEnoughInventory = false;
            return item;
        });
        return product;
    },
    
    /**
     * Process the items which are non discounted yet of inventory in terms of the amount
     * need to subtract of inventory or the Purchase order.
     *
     * NOTE: the same way as {@link _hydrateInventoryScopeEstimationByTask } this method mutate the
     * reference passed by param (make side-effect). In order to update by each parent iteration
     * the resultant amount.
     *
     * @param itemNonDiscountedYet
     * @param locationItemsAvailable
     * @param taskDate
     * @param purchaseItems
     * @return {*}
     */
    processItemNonDiscountedYet(itemNonDiscountedYet, locationItemsAvailable, taskDate, purchaseItems) {
        // get `item` and `itemId` given Item or Product item by parameter
        let {item, itemId} = this._getItemAndItemId(itemNonDiscountedYet);
        // search in the stock the amount of items available in all of locations
        const locationItemsResult = filter(locationItemsAvailable, locationItem =>
          locationItem.itemId === itemId ||
          get(locationItem, 'item.id') === itemId
        ) || {};
        const amountItemNeeded = item.amount;// is already multiplicand
        
        // calculate the amount of the stock in all location from one particular item
        const amountsLocation = map(locationItemsResult, locationItemResult => get(locationItemResult, 'amount'));
        const amountInStock = reduce(amountsLocation, (accumulateAmount, amount) => {
            return NumberUtils.sum(accumulateAmount, amount);
        });
        item.existItemInStock = NumberUtils.isGreater(amountInStock, 0);
        item.isEnoughInventory = NumberUtils.isGreaterOrEquals(amountInStock, amountItemNeeded);
        
        if (item.preventRecalculateWhereInventoryWasTaken) {
            // enter here means will be subtracted the item where the user already defined. More info take a look the comment of
            // method {@link this.turnOnWhereInventoryWasTakenNonChanged}
            const result = this.subtractAmountFromSpecificLocationItems(item, locationItemsAvailable);
            item = result.itemUpdated;
            locationItemsAvailable = result.locationItemsAvailable;
        } else {
            
            // initialize the amount left needed as initially the amount we want
            item.amountNeededLeft = cloneDeep(amountItemNeeded);
            item.whereInventoryWasTakenNew = []; // reset where was inventory taken
            
            if (item.existItemInStock) {
                // enter here means that exist item in stock so we need
                // to subtract the amount of stock from given location items available
                const result = this.subtractAmountFromLocationItems(item, locationItemsAvailable);
                item = result.itemUpdated;
                locationItemsAvailable = result.locationItemsAvailable;
            }
        }
        
        if (!item.isEnoughInventory) {
            // enter here means was not enough existing inventory
            // so we need to search in purchased order the amount item
            // needed and ,if is the case, add the amount purchase needed
            const result = this.subtractAmountFromPurchaseOrders(item, purchaseItems, taskDate);
            item = result.itemUpdated;
            purchaseItems = result.purchaseItemsUpdated;
        }
        
        return item;
    },
    
    /**
     * Persist new item in the application that will be available from the dashboard.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    addItem(item, success, failure) {
        InventoryServiceImpl.addItem(item, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new product in the application that will be available from the dashboard.
     * @param product
     * @param {Function} success
     * @param {Function} failure
     */
    addProduct(product, success, failure) {
        InventoryServiceImpl.addProduct(product, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new product in the application that will be available from the dashboard.
     * @param product
     * @param {Function} success
     * @param {Function} failure
     */
    duplicateProduct(product, success, failure) {
        InventoryServiceImpl.duplicateProduct(product, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new product in the application.
     * @param product
     * @param {Function} success
     * @param {Function} failure
     */
    addProductItem(productId, productItem, success, failure) {
        InventoryServiceImpl.addProductItem(productId, productItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persis new location item in the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    addLocationItem(locationItem, success, failure) {
        InventoryServiceImpl.addLocationItem(locationItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new product in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addIcon(icon, success, failure) {
        InventoryServiceImpl.addIcon(icon, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new vendor in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addVendor(vendor, success, failure) {
        InventoryServiceImpl.addVendor(vendor, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new vendor in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addVendorPurchase(vendorPurchase, success, failure) {
        InventoryServiceImpl.addVendorPurchase(vendorPurchase, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    addSale(sale, success, failure) {
        InventoryServiceImpl.addSale(sale, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    addSaleItem(sale, success, failure) {
        InventoryServiceImpl.addSaleItem(sale, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    addCustomer(customer, success, failure) {
        InventoryServiceImpl.addCustomer(customer, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    addCustomerAddress(customerId, customerAddress, success, failure) {
        InventoryServiceImpl.addCustomerAddress(customerId, customerAddress, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    addCustomerContact(customerId, customerAddress, success, failure) {
        InventoryServiceImpl.addCustomerContact(customerId, customerAddress, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    addCustomerItem(customerId, customerItem, success, failure) {
        InventoryServiceImpl.addCustomerItem(customerId, customerItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new purchase item in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addVendorPurchaseItem(vendorPurchaseItem, success, failure) {
        InventoryServiceImpl.addVendorPurchaseItem(vendorPurchaseItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Duplicate given vendor purchase
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    duplicateVendorPurchase(vendorPurchaseItem, success, failure) {
        InventoryServiceImpl.duplicateVendorPurchase(vendorPurchaseItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Mark Paid given vendor purchase
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
     markPaidVendorPurchase(vendorPurchaseId, success, failure) {
        InventoryServiceImpl.markPaidVendorPurchase(vendorPurchaseId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change Comment given vendor purchase
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
     saveCommentOnlyVendorPurchase(vendorPurchaseId, comment, success, failure) {
        InventoryServiceImpl.saveCommentOnlyVendorPurchase(vendorPurchaseId, comment, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new Vendor address in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addVendorAddress(vendorId, address, success, failure) {
        InventoryServiceImpl.addVendorAddress(vendorId, address, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new Vendor Contact in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addVendorContact(vendorId, contact, success, failure) {
        InventoryServiceImpl.addVendorContact(vendorId, contact, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new Vendor Item in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addVendorItem(vendorId, itemId, vendorItem, success, failure) {
        InventoryServiceImpl.addVendorItem(vendorId, itemId, vendorItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Persist new product in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    addLocation(werahouse, success, failure) {
        InventoryServiceImpl.addLocation(werahouse, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Send a comfirmation of purchase order items to the provider, to notify
     * that applicant want's to purchase those items.
     *
     * @param {Number} vendorPurchaseId
     * @param {Function} success
     * @param {Function} failure
     */
    sendVendorPurchaseOrder(vendorPurchaseId, success, failure) {
        InventoryServiceImpl.sendVendorPurchaseOrder(vendorPurchaseId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Will confirm and persist all items of given purchase id. Which means will be added
     * to the inventory after that.
     *
     * @param {Number} vendorPurchaseId
     * @param {Function} success
     * @param {Function} failure
     */
    confirmVendorPurchaseOrder(vendorPurchaseId, success, failure) {
        InventoryServiceImpl.confirmVendorPurchaseOrder(vendorPurchaseId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    confirmSaleOrderDelivered(saleId, success, failure) {
        InventoryServiceImpl.confirmSaleOrderDelivered(saleId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Notified to the customer their sale order.
     * @param {Number} saleId
     * @param {Function} success
     * @param {Function} failure
     */
    sendOrderToCustomer(saleId, success, failure) {
        InventoryServiceImpl.sendOrderToCustomer(saleId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Fetch items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchItems(pageNumber, success, failure) {
        InventoryServiceImpl.getItems(pageNumber, function () {
            let items = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(items);
            }
        }, failure);
    },

    /**
     * Fetch non inventory items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchNonInventoryItems(success, failure) {
        InventoryServiceImpl.getNonInventoryItems(function () {
            let items = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(items);
            }
        }, failure);
    },

    /**
     * Fetch items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchAllItems(success, failure) {
        InventoryServiceImpl.getAllItems(function () {
            let items = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(items);
            }
        }, failure);
    },

    /**
     * Fetch items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchItemsBy(filter, success, failure) {
        InventoryServiceImpl.getItemsBy(filter, function () {
            let items = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(items);
            }
        }, failure);
    },

    /**
     * Fetch items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchFinishProducts(success, failure) {
        InventoryServiceImpl.getFinishProducts(function () {
            let items = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(items);
            }
        }, failure);
    },

    /**
     * Fetch items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchFinishItems(success, failure) {
        InventoryServiceImpl.getFinishItems(function () {
            let items = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(items);
            }
        }, failure);
    },

    /**
     * Fetch vendor items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchVendorItems(vendorId, success, failure) {
        InventoryServiceImpl.getVendorItems(vendorId, function () {
            let items = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(items);
            }
        }, failure);
    },

    /**
     * Fetch vendor items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchVendorPurchases(currentPage, success, failure) {
        InventoryServiceImpl.getVendorPurchases(currentPage, function () {
            let purchases = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(purchases);
            }
        }, failure);
    },

    /**
     * Fetch vendor items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchVendorPurchasesWithTotalPrice(currentPage, success, failure) {
        InventoryServiceImpl.getVendorPurchasesWithTotalPrice(currentPage, function () {
            let purchases = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(purchases);
            }
        }, failure);
    },

    /**
     * Fetch vendor items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchVendorPurchasesBy(filter, success, failure) {
        InventoryServiceImpl.getVendorPurchasesBy(filter, function () {
            let purchases = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(purchases);
            }
        }, failure);
    },

    /**
     * Sales from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchSales(page, success, failure) {
        InventoryServiceImpl.getSales(page, function () {
            let sales = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(sales);
            }
        }, failure);
    },

    /**
     * Sales from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchSalesBy(filter, success, failure) {
        InventoryServiceImpl.getSalesBy(filter, function () {
            let sales = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(sales);
            }
        }, failure);
    },

    /**
     * Sales from the server.
     * @param {Number} SaleId
     * @param {Function} success
     * @param {Function} failure
     */
    fetchSaleItems(saleId, success, failure) {
        InventoryServiceImpl.getSaleItems(saleId, function () {
            let sales = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(sales);
            }
        }, failure);
    },

    /**
     * Change Comment given sale order
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
     saveCommentOnlySaleOrder(saleId, comment, success, failure) {
        InventoryServiceImpl.saveCommentOnlySaleOrder(saleId, comment, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Mark Paid given Sale
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
     markPaidSale(saleId, success, failure) {
        InventoryServiceImpl.markPaidSale(saleId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Customers from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchCustomers(success, failure) {
        InventoryServiceImpl.getCustomers(function () {
            let sales = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(sales);
            }
        }, failure);
    },

    /**
     * Retrieve the customers contacts from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchCustomerContacts(customerId, success, failure) {
        InventoryServiceImpl.getCustomerContacts(customerId, function () {
            let customerAddresses = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(customerAddresses);
            }
        }, failure);
    },

    fetchCustomerAddresses(customerId, success, failure) {
        InventoryServiceImpl.getCustomerAddresses(customerId, function () {
            let customerAddresses = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(customerAddresses);
            }
        }, failure);
    },

    fetchCustomerItems(customerId, success, failure) {
        InventoryServiceImpl.getCustomerItems(customerId, function () {
            let customerAddresses = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(customerAddresses);
            }
        }, failure);
    },

    fetchVendorPurchasesItem(vendorId, success, failure) {
        InventoryServiceImpl.getVendorPurchasesItem(vendorId, function () {
            let purchases = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(purchases);
            }
        }, failure);
    },

    fetchIcons(success, failure) {
        InventoryServiceImpl.getIcons(function () {
            let icons = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(icons);
            }
        }, failure);
    },

    downloadReportXLS(filter, controllerName, success, failure) {
        InventoryServiceImpl.downloadReportXLS(filter, controllerName, function () {
            
            if (!success) {
                success = () => {};
            }
    
            if (!failure) {
                failure = () => {};
            }
            
            if (this.status <= 201) {
                success.bind(this)(this.response);
            } else {
                failure.bind(this)(this.response);
            }
        }, failure);
    },

    downloadReportPDF(filter, controllerName, success, failure) {
        InventoryServiceImpl.downloadReportPDF(filter, controllerName, function () {
            
            if (!success) {
                success = () => {};
            }
    
            if (!failure) {
                failure = () => {};
            }
            
            if (this.status <= 201) {
                success.bind(this)(this.response);
            } else {
                failure.bind(this)(this.response);
            }
        }, failure);
    },

    /**
     * Fetch icons from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchLocation(success, failure) {
        InventoryServiceImpl.getLocation(function () {
            let locations = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(locations);
            }
        }, failure);
    },

    /**
     * Fetch Vendor from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchVendor(success, failure) {
        InventoryServiceImpl.getVendor(function () {
            let vendorContent = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(vendorContent);
            }
        }, failure);
    },

    /**
     * Fetch Vendor Addresses from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchVendorAddresses(vendorId, success, failure) {
        InventoryServiceImpl.getVendorAddresses(vendorId, function () {
            let addresses = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(addresses);
            }
        }, failure);
    },

    /**
     * Fetch Vendor Contact from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchVendorContacts(vendorId, success, failure) {
        InventoryServiceImpl.getVendorContacts(vendorId, function () {
            let addresses = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(addresses);
            }
        }, failure);
    },

    /**
     * Fetch icons by items from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchIconByProduct(productId, success, failure) {
        InventoryServiceImpl.getIconByProduct(productId, function () {
            let icons = ContentUtils.parse(this.responseText) || [];

            if (success) {
                success.bind(this)(icons);
            }
        }, failure);
    },

    /**
     * Fetch product from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchProducts(success, failure) {
        InventoryServiceImpl.getProducts(function () {
            let products = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(products);
            }
        }, failure);
    },

    /**
     * Fetch items of given product product id from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchItemsAddition(productId, success, failure) {
        const isAddition = true;
        InventoryServiceImpl.getItemsByProduct(productId, isAddition, function () {
            let products = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(products);
            }
        }, failure);
    },

    /**
     * Fetch items of given product product id from the server.
     * @param {Function} success
     * @param {Function} failure
     */
    fetchItemsSubtraction(productId, success, failure) {
        const isAddition = false;
        InventoryServiceImpl.getItemsByProduct(productId, isAddition, function () {
            let products = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(products);
            }
        }, failure);
    },

    /**
     * Fetch items of given product product id from the server.
     * @param {Number} pageNumber
     * @param {Function} success
     * @param {Function} failure
     */
    fetchLocationItems(pageNumber, success, failure) {
        InventoryServiceImpl.getItemsLocation(pageNumber, function () {
            const result = ContentUtils.parse(this.responseText) || [];
            const locationItems = result.locationItems;
            const locations = result.locations;
            const items = result.items;
            if (success) {
                success.bind(this)(items, locations, locationItems);
            }
        }, failure);
    },

    /**
     * Fetch items of given product product id from the server.
     * @param {Number} pageNumber
     * @param {Function} success
     * @param {Function} failure
     */
    fetchLocationItemsWithTotalAmount(pageNumber, success, failure) {
        InventoryServiceImpl.getLocationItemsWithTotalAmount(pageNumber, function () {
            let locationItems = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(locationItems);
            }
        }, failure);
    },

    /**
     * Fetch items of given product product id from the server.
     * @param {Number} pageNumber
     * @param {Function} success
     * @param {Function} failure
     */
    fetchLocationItemsWithTotalAmountBy(filter, success, failure) {
        InventoryServiceImpl.getLocationItemsWithTotalAmountBy(filter, function () {
            let locationItems = ContentUtils.parse(this.responseText) || [];
            if (success) {
                success.bind(this)(locationItems);
            }
        }, failure);
    },

    /**
     * Change persistent item in the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    changeItem(item, success, failure) {
        InventoryServiceImpl.changeItem(item, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change persistent location item in the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    changeLocationItem(locationItem, success, failure) {
        InventoryServiceImpl.changeLocationItem(locationItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change persistent item in the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendorAddress(vendorId, address, success, failure) {
        InventoryServiceImpl.changeVendorAddress(vendorId, address, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change persistent Vendor Contact in the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendorContact(vendorId, contact, success, failure) {
        InventoryServiceImpl.changeVendorContact(vendorId, contact, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change Vendor Item in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendorItem(vendorId, itemId, vendorItem, success, failure) {
        InventoryServiceImpl.changeVendorItem(vendorId, itemId, vendorItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change Vendor purchase in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendorPurchase(vendorPurchase, success, failure) {
        InventoryServiceImpl.changeVendorPurchase(vendorPurchase, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change existing Sale
     * @param Sale
     * @param {Function} success
     * @param {Function} failure
     */
    changeSale(sale, success, failure) {
        InventoryServiceImpl.changeSale(sale, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change Amount Paid PO
     * @param vendorPurchase
     * @param {Function} success
     * @param {Function} failure
     */
     changeAmountPaidPO(vendorPurchase, value, success, failure) {
        InventoryServiceImpl.changeAmountPaidPO(vendorPurchase, value, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },


    /**
     * Change Amount Paid SO
     * @param Sale
     * @param {Function} success
     * @param {Function} failure
     */
     changeAmountPaidSO(sale, success, failure) {
        InventoryServiceImpl.changeAmountPaidSO(sale, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change existing SaleItem
     * @param SaleItemc
     * @param {Function} success
     * @param {Function} failure
     */
    changeSaleItem(saleItem, success, failure) {
        InventoryServiceImpl.changeSaleItem(saleItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change existing Sale
     * @param Customer
     * @param {Function} success
     * @param {Function} failure
     */
    changeCustomer(customer, success, failure) {
        InventoryServiceImpl.changeCustomer(customer, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    changeCustomerAddress(customerId, address, success, failure) {
        InventoryServiceImpl.changeCustomerAddress(customerId, address, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    changeCustomerContact(customerId, contact, success, failure) {
        InventoryServiceImpl.changeCustomerContact(customerId, contact, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    changeCustomerItem(customerId, customerItem, success, failure) {
        InventoryServiceImpl.changeCustomerItems(customerId, customerItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    deleteCustomerContact(customerId, customerContactId, success, failure) {
        InventoryServiceImpl.deleteCustomerContact(customerId, customerContactId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    deleteCustomerAddress(customerId, customerAddressId, success, failure) {
        InventoryServiceImpl.deleteCustomerAddress(customerId, customerAddressId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    deleteCustomerItems(customerId, customerItemId, success, failure) {
        InventoryServiceImpl.deleteCustomerItems(customerId, customerItemId, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change Vendor Item in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendorPurchaseItem(vendorPurchaseItem, success, failure) {
        InventoryServiceImpl.changeVendorPurchaseItem(vendorPurchaseItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change Vendor Item purchase received in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendorPurchaseItemReceived(vendorPurchaseItem, success, failure) {
        InventoryServiceImpl.changeVendorPurchaseItemReceived(vendorPurchaseItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change Vendor Item purchase received in the application.
     * @param icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendorPurchaseItemReceivedDraft(vendorPurchaseItem, success, failure) {
        InventoryServiceImpl.changeVendorPurchaseItemReceivedDraft(vendorPurchaseItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change persistent vendor in the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    changeVendor(vendor, success, failure) {
        InventoryServiceImpl.changeVendor(vendor, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change persistent product in the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    changeProduct(product, success, failure) {
        InventoryServiceImpl.changeProduct(product, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change existing product item in the application.
     * @param product
     * @param {Function} success
     * @param {Function} failure
     */
    changeProductItem(productId, productItem, success, failure) {
        InventoryServiceImpl.changeProductItem(productId, productItem, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change existing icon in the application.
     * @param {Object} icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeIcon(iconsChanged, success, failure) {
        InventoryServiceImpl.changeIcon(iconsChanged, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change existing location in the application.
     * @param {Object} icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeLocation(location, success, failure) {
        InventoryServiceImpl.changeLocation(location, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Change existing icon in the application.
     * @param {Object} icon
     * @param {Function} success
     * @param {Function} failure
     */
    changeIconsFromProduct(productIdSelected, iconsChanged, success, failure) {
        InventoryServiceImpl.changeIconFromProduct(productIdSelected, iconsChanged, function () {
            if (success) {
                success(this);
            }
        }, failure);
    },

    /**
     * Delete items from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteItem(item, success, failure) {
        InventoryServiceImpl.deleteItem(item, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete sale from the application.
     * @param saleId
     * @param {Function} success
     * @param {Function} failure
     */
    deleteSale(saleId, success, failure) {
        InventoryServiceImpl.deleteSale(saleId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete sale from the application.
     * @param saleId
     * @param saleItemId
     * @param {Function} success
     * @param {Function} failure
     */
    deleteSaleItem(saleId, saleItemId, success, failure) {
        InventoryServiceImpl.deleteSaleItem(saleId, saleItemId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete customer from the application.
     * @param saleId
     * @param {Function} success
     * @param {Function} failure
     */
    deleteCustomer(customerId, success, failure) {
        InventoryServiceImpl.deleteCustomer(customerId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete location items from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteLocationItem(locationItemId, success, failure) {
        InventoryServiceImpl.deleteLocationItem(locationItemId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete icon from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteIcon(item, success, failure) {
        InventoryServiceImpl.deleteIcon(item, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete location from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteLocation(locationId, success, failure) {
        InventoryServiceImpl.deleteLocation(locationId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete vendor from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteVendor(vendorId, success, failure) {
        InventoryServiceImpl.deleteVendor(vendorId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete vendor from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteVendorAddress(vendorId, addressId, success, failure) {
        InventoryServiceImpl.deleteVendorAddress(vendorId, addressId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete vendor from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteVendorContact(vendorId, contactId, success, failure) {
        InventoryServiceImpl.deleteVendorContact(vendorId, contactId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete vendor from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteVendorItem(vendorId, itemId, success, failure) {
        InventoryServiceImpl.deleteVendorItem(vendorId, itemId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete items from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteProductItem(item, success, failure) {
        InventoryServiceImpl.deleteProductItem(item, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete Product from the application.
     * @param item
     * @param {Function} success
     * @param {Function} failure
     */
    deleteProduct(product, success, failure) {
        InventoryServiceImpl.deleteProduct(product, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete the given purchase id.
     * @param purchaseId
     * @param success
     * @param failure
     */
    deletePurchase(purchaseId, success, failure) {
        InventoryServiceImpl.deletePurchase(purchaseId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    /**
     * Delete the given purchase item id.
     * @param purchaseItemId
     * @param success
     * @param failure
     */
    deletePurchaseItem(purchaseItemId, success, failure) {
        InventoryServiceImpl.deletePurchaseItem(purchaseItemId, function () {
            if (success) {
                success();
            }
        }, failure);
    },

    isApplicantIcon(icon) {
        return !!(icon && icon.iconOverridden);
    },

    isAlreadyInUseDefaultIcon(defaultIcon, customIconsByApplicant) {
        return some(customIconsByApplicant, customIcon => customIcon.iconId === defaultIcon.id);
    },

    filterOnlyProductIconsChanged(icons, iconsPristine) {
        let iconsChanged = [];
        for (const iconToValidate of icons) {
            const pristineIcon = find(iconsPristine, icon => get(icon, 'iconDetail.name') === get(iconToValidate, 'iconDetail.name'));

            if (!this.isProductIconPristine(iconToValidate, pristineIcon)) {
                iconsChanged.push(iconToValidate);
            }
        }
        return iconsChanged;
    },

    discountInventoryAlreadyReserved(orders, locationItemsAvailable) {
        return locationItemsAvailable;
    },


    calculateTotalVolumeOfProduct(order) {

        if (!order) {
            return null;
        }

        if (order.isSingle) {
            return _calculateTotalVolumeOfProduct(union(order.singleTasks, order.batchs));
        } else {
            return _calculateTotalVolumeOfProduct(union(order.fermenterTasks, order.packagingTasks, order.batchs));
        }
    },

    calculateTotalVolumeFromTask(tasks) {
        return _calculateTotalVolumeOfProduct(tasks);
    },

    calculateTotalVolumeOfProductLeft(order, totalBatchVolume = 0) {
        if (order) {
            const orderVolume = get(order, 'volume', get(order, 'orderVolume', 0));
            return NumberUtils.subtract(orderVolume, this.calculateTotalVolumeOfProduct(order));
        } else {
            // enter here means don't have product so means to consume all total volume
            return totalBatchVolume;
        }
    },

    calculateTotalVolumeLeft(tasks, batchVolume) {
        return NumberUtils.subtract(batchVolume, _calculateTotalVolumeOfProduct(tasks));
    },

    filterOnlyIconsChanged(icons, iconsPristine) {
        let iconsChanged = [];
        for (const iconToValidate of icons) {
            const pristineIcon = find(iconsPristine, icon => get(icon, 'iconDetail.name') === get(iconToValidate, 'iconDetail.name'));

            if (!this.isIconPristine(iconToValidate, pristineIcon)) {
                iconsChanged.push(iconToValidate);
            }
        }
        return iconsChanged;
    },

    isIconPristine(icon, iconChanged) {
        return get(icon, 'iconDetail.isBrewingAllowed') === get(iconChanged, 'iconDetail.isBrewingAllowed') &&
            get(icon, 'iconDetail.isFermentingAllowed') === get(iconChanged, 'iconDetail.isFermentingAllowed') &&
            get(icon, 'iconDetail.isPackagingAllowed') === get(iconChanged, 'iconDetail.isPackagingAllowed');
    },

    isProductIconPristine(icon, iconChanged) {
        return get(icon, 'isProductIconChecked') === get(iconChanged, 'isProductIconChecked');
    },

    formatIconsBySections(allDefaultIcons, customIconsFromApplicant) {
        const allDefaultIconsWithoutUse = filter(allDefaultIcons, defaultIcon => !this.isAlreadyInUseDefaultIcon(defaultIcon, customIconsFromApplicant));

        // mark as checked
        customIconsFromApplicant = map(customIconsFromApplicant, customIcon => {
            customIcon.isChecked = true;
            return customIcon;
        });

        const allIconsWithCustomIconsFromApplicant = concat(allDefaultIconsWithoutUse, customIconsFromApplicant);

        const brewingIcons = filter(allIconsWithCustomIconsFromApplicant, icon => get(icon, 'iconDetail.isBrewingAllowed'));
        const fermentingIcons = filter(allIconsWithCustomIconsFromApplicant, icon => get(icon, 'iconDetail.isFermentingAllowed'));
        const packagingIcons = filter(allIconsWithCustomIconsFromApplicant, icon => get(icon, 'iconDetail.isPackagingAllowed'));

        return {
            Brewing: brewingIcons,
            Fermenting: fermentingIcons,
            Packaging: packagingIcons
        };
    },

    /**
     * The idea is each task from one Order, need to contain all information of the products
     * belong to it. Also each Product have the items which need to consume in inventory module.
     * @param tasks
     * @param beerStyleId
     * @return {*}
     */
    hydrateTaskWithProducts(tasks, beerStyleId) {
        if (!SessionStore.isFullPaidVersion()) {
            return tasks;
        }

        let result = [];
        const filteredProducts = DashboardServiceCache.getProductsByBeerStyle(beerStyleId);
        const filteredProductsItems = DashboardServiceCache.getProductItemsByProducts(filteredProducts);
        const filteredProductIcons = DashboardServiceCache.getProductIconsProducts(filteredProducts);

        if (Array.isArray(tasks)) {
            result = map(tasks, task => _hydrateTaskWithProducts(task, beerStyleId, filteredProducts, filteredProductIcons, filteredProductsItems));
        } else {
            result = _hydrateTaskWithProducts(tasks, beerStyleId, filteredProducts, filteredProductIcons, filteredProductsItems);
        }
        return _sortItemsFromProductsAndFilterDisabled(result);
    },

    populateEachProductAmount(order, taskFieldName, beerStyle, maxTankVolume = -1) {
        const tasks = get(order, taskFieldName);
        const tasksWithoutProducts = this.filterTaskWithoutProducts(tasks);
        const tasksWithProductsConfiguration = this.filterTaskWithSomeProductConfiguration(tasks);
        const tasksWithProductsConfigurationAmount = get(tasksWithProductsConfiguration, 'length') || 0;
        const orderVolume = get(order, 'volume');
        const isBatchSections = taskFieldName === 'batchs';
        // the brewing batch should be spllited by the total volume;
        let volumeByTask = isBatchSections ? NumberUtils.divide(orderVolume, tasksWithProductsConfigurationAmount) : orderVolume;

        if (maxTankVolume && NumberUtils.isGreater(maxTankVolume, volumeByTask)) {
            // enter here means that we define as maximum volume value the tank,
            // because we don't want to auto-populate with some volume higher that the tank could support.
            volumeByTask = maxTankVolume;
        }

        if (ContentUtils.isEmptyArray(tasks) || NumberUtils.isLessOrEquals(orderVolume, '0')) {
            // enter here means we don't need to populate anything
            return order;
        }

        order[taskFieldName] = union(map(tasksWithProductsConfiguration, task => {
            const taskName = get(task, 'task') || get(task, 'name');

            // so we search by default brewing icon
            const products = DashboardServiceCache.getProductByIconNameEnables(taskName);
            const productTarget = find(products, product => get(product, 'beerStyleName') === get(beerStyle, 'name'));

            if (productTarget) {
                const productVolume = NumberUtils.getNumber(get(productTarget, 'volume')) === 0 ? 1 : get(productTarget, 'volume');
                const productItems = DashboardServiceCache.getProductItemsByProductId(productTarget.id);
                // calculate the amount of product needed
                const amountProductNeeded = NumberUtils.divide(volumeByTask, productVolume);

                productTarget.amount = amountProductNeeded;
                productTarget.volume = NumberUtils.multiplyAndTruncate(productTarget.amount, productVolume);
                productTarget.isNeededCalculateInventory = true; // this flag is to indicate that this product needed to calculate
                productTarget.task = task; // later remove this to avoid cycle references
                productTarget.items = _multiplyAmountOfItems(productItems, productTarget.amount);

                if (ContentUtils.isEmptyArray(task.products)) {
                    task.products = [productTarget];
                } else {
                    task.products = ContentUtils.replaceByAttributes(task.products, productTarget, 'id'); //update in case that exist a new one
                }
                task.products = ContentUtils.removeDuplicateByAttribute(task.products, ['id']);

            }

            return task;

        }), tasksWithoutProducts);

        return order;
    },

    filterTaskWithSomeProductConfiguration(tasks) {
        const productIcons = DashboardServiceCache.getProductIcons();
        return filter(tasks, task => {
            const taskName = get(task, 'task') || get(task, 'name');
            const haveIcon = taskName && taskName !== 'no-icon';
            const matchSomeProduct = some(productIcons, productIcon =>
              get(productIcon, 'icon.iconDetail.name') === taskName ||
              get(productIcon, 'icon.iconDetail.displayName') === taskName
            );
            return haveIcon && matchSomeProduct;
        });
    },

    filterTaskWithoutProducts(tasks) {
        const productIcons = DashboardServiceCache.getProductIcons();
        return filter(tasks, task => {
            const taskName = get(task, 'task') || get(task, 'name');
            const haveIcon = taskName && taskName !== 'no-icon';
            const matchSomeProduct = some(productIcons, productIcon =>
              get(productIcon, 'icon.iconDetail.name') === taskName ||
              get(productIcon, 'icon.iconDetail.displayName') === taskName
            );
            return !(haveIcon && matchSomeProduct);
        });
    },

    hydrateTaskWithProductsAmount(nextState) {
        if (SessionStore.isFullPaidVersion()) {
            const order = get(nextState, 'order');
            if (!order.volume) {
                order.volume = get(nextState, 'orderVolume');
            }
            const beerStyle = get(nextState, 'styleBeerSelected');
            const haveBrewing = ContentUtils.isNotEmptyArray(get(order, 'batchs'));

            if (haveBrewing) {
                nextState.order = this.populateEachProductAmount(order, 'batchs', beerStyle);
            }

            const isSingleOrder = get(order, 'isSingle') || get(nextState, 'singleTasks.length') > 0;

            if (isSingleOrder) {
                order.singleTasks = get(nextState, 'singleTasks');
                nextState.order = this.populateEachProductAmount(order, 'singleTasks', beerStyle);
                nextState.singleTasks = get(nextState, 'order.singleTasks');
            } else {
                order.fermenterTasks = get(nextState, 'fermenterTasks');
                order.packagingTasks = get(nextState, 'packagingTasks');
                nextState.order = this.populateEachProductAmount(order, 'fermenterTasks', beerStyle);
                nextState.order = this.populateEachProductAmount(order, 'packagingTasks', beerStyle);
                nextState.fermenterTasks = get(nextState, 'order.fermenterTasks');
                nextState.packagingTasks = get(nextState, 'order.packagingTasks');
            }
        }
        return nextState;
    },


    _filterScopeInventory(allSortedTasks, startingScopeInventoryDate, endingScopeInventoryDate) {
        if (startingScopeInventoryDate) {
            if (endingScopeInventoryDate) {
                return filter(allSortedTasks, task => DateUtils.isAfterOrEquals(task.date, startingScopeInventoryDate) && DateUtils.isBefore(endingScopeInventoryDate));
            } else {
                return filter(allSortedTasks, task => DateUtils.isAfterOrEquals(task.date, startingScopeInventoryDate));
            }
        }
        return allSortedTasks;
    },

    _filterPurchaseOrderOrdered(purchaseItems, allItemsId) {
        const alreadyPurchaseItems = filter(purchaseItems, vendorPurchase => Constants.PURCHASE_STATE_DASHBOARD_COUNT_AS_ORDERED.indexOf(get(vendorPurchase, 'state')) > -1);
        return filter(alreadyPurchaseItems, purchaseItem => allItemsId.indexOf(get(purchaseItem, 'itemId')) > -1);
    },

    _filterLocationItemNeeded(allTasks, locationItems, allItemsId) {
        return filter(locationItems, locationItem => allItemsId.indexOf(get(locationItem, 'item.id')) > -1);
    },

    /**
     * Given task retrieve their inventory key string state.
     *
     * @param task
     * @param maxScopeDate
     * @returns {string}
     */
    getInventoryStatusFromTask(task, maxScopeDate = SessionStore.getMaxScopeInventoryDate()) {
        const haveAtLeastOneProduct = this.haveAProducts(task);
        
        if (!haveAtLeastOneProduct) {
            return '';
        }

        if (this.isEveryProductAlreadyDiscountedAndConfirmed(task)) {
            // green with white check
            return Constants.INVENTORY_ALL_DISCOUNTED_AND_CONFIRMED;
        }

        if (this.isSomeProductNonConfirmedIsOutOfInventoryScope(task, maxScopeDate)) {
            // RED WITH ADMIRATION WHITE
            return Constants.INVENTORY_SOME_PRODUCT_NON_CONFIRMED_OUT_SCOPE;
        }

        if (this.isSomeItemsIsEnoughInPurchase(task)) {
            // yellow
            return Constants.INVENTORY_MISSING_ITEMS_BUT_PURCHASE_IN_COMING;
        }

        if (this.isSomeItemsIsNotEnoughInStock(task)) {
            // red
            return Constants.INVENTORY_MISSING_ITEMS;
        }

        if (this.isSomeProductWasUnconfirmedAlreadyDiscounted(task)) {
            // green
            return Constants.INVENTORY_SOME_WAS_UNCONFIRMED_ALREADY_DISCOUNTED;
        }

        if (this.isSomeProductAlreadyConfirmedAndSomeNotConfirmed(task)) {
            // green
            return Constants.INVENTORY_SOME_CONFIRMED_AND_SOME_UNCONFIRMED;
        }

        if (this.isEnoughInventory(task)) {
            // green
            return Constants.INVENTORY_IS_ENOUGH_INVENTORY;
        }
    },

    isEnoughInventory(task) {
        return get(task, 'someItemsIsNotEnoughInStock') === false;
    },

    isSomeProductNonConfirmedIsOutOfInventoryScope(task, maxScopeDate) {
        const isSomeNonConfirmedProduct = filter(get(task, 'products'), product => !get(product, 'isConfirmed'));

        return isSomeNonConfirmedProduct && DateUtils.isBefore(get(task, 'date'), maxScopeDate);
    },

    isSomeProductAlreadyConfirmedAndSomeNotConfirmed(task) {
        const someIsUnConfirmed = some(get(task, 'products'), product => !get(product, 'isConfirmed'));
        const someIsConfirmed = some(get(task, 'products'), product => get(product, 'isConfirmed'));

        return someIsUnConfirmed && someIsConfirmed;
    },

    isSomeProductWasUnconfirmedAlreadyDiscounted(task) {
        return some(get(task, 'products'), product => !get(product, 'isConfirmed') && get(product, 'isDiscounted'));
    },

    isSomeItemsIsNotEnoughInStock(task) {
        return some(get(task, 'products'), product => get(product, 'someItemsIsNotEnoughInStock') === true);
    },

    isSomeItemsIsEnoughInPurchase(task) {
        return some(get(task, 'products'), product => get(product, 'someItemsIsNotEnoughInStock') === true && get(product, 'someItemsIsNotEnoughInPurchase') === false);
    },

    isSomeProductAlreadyCalculated(products) {
        return some(products, product => get(product, 'isNeededCalculateInventory') === false);
    },

    isEveryProductAlreadyDiscountedAndConfirmed(task) {
        const products = get(task, 'products') || [];
        return every(products, product => {
            return get(product, 'isDiscounted');
        });
    },

    returnItemsConsumed(product, locationItems) {
        if (product) {
            const productItems = get(product, 'items') || [];
            for (const productItem of productItems) {
                const locationItemTarget = find(locationItems, locationItem => _getItemId(locationItem) === _getItemId(productItem));

                if (locationItemTarget) {
                    const amountsSubtract = (get(productItem, 'whereInventoryWasTakenSaved') || [])
                        .map(whereInventoryWasSubtracted => get(whereInventoryWasSubtracted, 'amountSubtracted', 0));
                    const amountToReturn = ContentUtils.isNullOrEmpty(amountsSubtract) ? '0' : reduce(amountsSubtract, (total, amountSubtracted) => NumberUtils.sum(total, amountSubtracted));
                    const existingLocationItemAmount = get(locationItemTarget, 'amount', '0');
                    const newAmount = NumberUtils.sum(existingLocationItemAmount, amountToReturn);
                    locationItemTarget.amount = newAmount;
                    locationItems = ContentUtils.replaceByAttributes(locationItems, locationItemTarget, ['item.id', 'location.id']);
                }
            }
        }

        return locationItems;
    },

    getItemsIdNeedCalculateReservation(task, locationItems) {
        const products = get(task, 'products') || [];
        const productsWithAmount = get(products, product => product.amount);
        const items = filter(flatten(map(productsWithAmount, product => get(product, 'items'))), item => item);
        const itemsId = map(items, item => get(item, 'itemId'));
        const itemsFromLocationItemsId = map(locationItems, locationItem => get(locationItem, 'item.id'));
        return filter(itemsId, itemId => itemsFromLocationItemsId.indexOf(itemId) > -1);
    },

    /**
     * Update the amount of the location items tacking care the already amount reserved (Non published yet).
     *
     * Note: {@link the Product.whereInventoryWasTakenSaved} as reference those items which was published. So
     * we don need it, because at that particular moment, the location items was affected and updated. So it does not need
     * to simulate their reservation anymore because was physically discounted from the inventory.
     *
     * @param products
     * @param locationItemsAvailable
     */
    updateLocationItemsWithProducts(products = [], locationItemsAvailable) {
    
        if (ContentUtils.isEmptyArray(products)) {
            return locationItemsAvailable;
        }
    
        const itemsIdOfProducts = flatMap(products, (product => product.items)).map(item => item.itemId) || [];
        const noRepetedItemOfProducts = uniq(itemsIdOfProducts);
    
        return map(locationItemsAvailable, locationItemAvailable => {
        
            const locationItemRelatedOfSomeItemOfProducts = noRepetedItemOfProducts.indexOf(get(locationItemAvailable,'item.id')) > -1;
        
            if (ContentUtils.isNotEmptyArray(products) && locationItemRelatedOfSomeItemOfProducts) {
            
                const productsShouldSubtract = filter(products, product => !(product.isDiscounted && !product.isConfirmed && !product.isSaved));
                const productsShouldAdd = filter(products, product => product.isDiscounted && !product.isConfirmed && !product.isSaved);
                const itemsShouldSubtract = flatten(map(productsShouldSubtract, product => product.items));
                const itemsShouldAdd = flatten(map(productsShouldAdd, product => product.items));
                // notice that we need to discount here the inventory which is already taken from location items
                const whereInventoryWasTakenNewSubtract = filter(flatten(map(itemsShouldSubtract, item => get(item, 'whereInventoryWasTakenNew'))), info => info);
                const whereInventoryWasTakenNewAdd = filter(flatten(map(itemsShouldAdd, item => get(item, 'whereInventoryWasTakenNew'))), info => info);
                const whereInventoryWasTakenNewFilteredSubtract = filter(whereInventoryWasTakenNewSubtract, info => get(info, 'locationItemId') === get(locationItemAvailable, 'id'));
                const whereInventoryWasTakenNewFilteredAdd = filter(whereInventoryWasTakenNewAdd, info => get(info, 'locationItemId') === get(locationItemAvailable, 'id'));
            
                if (ContentUtils.isNotEmptyArray(whereInventoryWasTakenNewFilteredSubtract)) {
                    const subtractedAmounts = map(whereInventoryWasTakenNewFilteredSubtract, item => get(item, 'amountSubtracted'));
                    const totalAmountSubtracted = reduce(subtractedAmounts, (num, total) => NumberUtils.sum(num, total));
                    locationItemAvailable.amount = NumberUtils.subtract(locationItemAvailable.amount, totalAmountSubtracted);
                }
            
                if (ContentUtils.isNotEmptyArray(whereInventoryWasTakenNewFilteredAdd)) {
                    const subtractedAmounts = map(whereInventoryWasTakenNewFilteredAdd, item => get(item, 'amountSubtracted'));
                    const totalAmountAdded = reduce(subtractedAmounts, (num, total) => NumberUtils.sum(num, total));
                    locationItemAvailable.amount = NumberUtils.sum(locationItemAvailable.amount, totalAmountAdded);
                }
            }
            return locationItemAvailable;
        });
    },

    isSomeItemPurchased(purchaseItems, itemId) {
        const purchaseItemOrdered = filter(purchaseItems, purchaseItem => purchaseItem.state !== 'PENDING');

        return some(purchaseItemOrdered, purchaseItem => {

            const amountRequested = get(purchaseItem, 'amountRequestedUpdated', get(purchaseItem, 'amountRequested'));
            const isItemPurchased = get(purchaseItem, 'itemId') === itemId;
            const isPurchasedAmountGratherThanZero = NumberUtils.isGreater(amountRequested, 0);

            return isItemPurchased && isPurchasedAmountGratherThanZero;
        });
    },

    isSomeItemAlreadyPurchased(purchaseItems) {
        const purchaseItemOrdered = filter(purchaseItems, purchaseItem => purchaseItem.state !== 'PENDING');

        return some(purchaseItemOrdered, purchaseItem => {
            const allWhereInventoryWasTaken = concat(get(purchaseItem, 'whereInventoryWasTakenNew'), get(purchaseItem, 'whereInventoryWasTakenSaved'));
            return some(allWhereInventoryWasTaken, info => NumberUtils.isGreater(get(info, 'amountPurchased'), 0));
        });
    },

    /**
     * Subtract the amount of the purchase order because. At business level we try to discount
     * before to inventory and didn't was enough.
     *
     * @param item
     * @param purchaseItems
     * @param taskDate
     * @return {*}
     */
    subtractAmountFromPurchaseOrders(item, purchaseItems, taskDate) {
        const itemId = item.itemId;

        // validate if have at lest one purchase item which the item needed
        item.isPurchased = this.isSomeItemPurchased(purchaseItems, itemId);
        item.isPurchasedAmountEnough = false; // by default, then we validate if we have enough or not

        let foundPurchaseItemsTarget = false;

        // sort by first higher amount request
        // in order to optimize the searching request
        const purchaseItemsFilterByItemId = filter(ContentUtils.filterEnable(purchaseItems),
            purchaseItem => get(purchaseItem, 'itemId') === itemId &&
                NumberUtils.isGreater(get(purchaseItem, 'amountRequestedUpdated', get(purchaseItem, 'amountRequested')), 0)) || [];
        const purchaseItemsByAmountRequestDesc = this.sortPurchaseItemByAmountRequested(purchaseItemsFilterByItemId);

        // iterate purchase items instead of filter first
        // because we want to change amount reference of original purchase item list
        for (let purchaseItem of purchaseItemsByAmountRequestDesc) {

            if (purchaseItem.itemId !== itemId) {
                continue;
            }

            // check first if items which was purchase arrive
            // before that we needed
            if (!_isPurchaseItemArrive(taskDate, purchaseItem)) {
                continue;
            }

            if (ContentUtils.isEmptyArray(item.whereInventoryWasTakenNew)) {
                item.whereInventoryWasTakenNew = [];
            }

            const {purchaseItemUpdated, amountPurchased, haveEnoughPurchaseItems} = this.processAmountSubtractedFromPurchaseItem(purchaseItem, item.amountNeededLeft);
            purchaseItem = purchaseItemUpdated;

            // the amount subtracted from the purchase
            const amountSubtractedFromPurchase = haveEnoughPurchaseItems ? item.amountNeededLeft : amountPurchased;

            // save record about amount left remaining
            item.amountNeededLeft = haveEnoughPurchaseItems ? 0 : NumberUtils.subtract(item.amountNeededLeft, amountPurchased);

            if (NumberUtils.isGreater(amountSubtractedFromPurchase, '0')) { // don't make sense if don't subtract anything
                // save the operation
                item.whereInventoryWasTakenNew.push({
                    itemId: get(item, 'item.id'),
                    type: 'VENDOR_PURCHASE_ITEM_MODULE',
                    amountSubtracted: amountSubtractedFromPurchase,
                    vendorPurchaseItemId: purchaseItem.id,
                    code: purchaseItem.code,
                    purchaseId: purchaseItem.purchaseId,
                    amountPurchased,
                    amountAfter: purchaseItem.amountRequestedUpdated,
                    amountItemNeededLeft: cloneDeep(item.amountNeededLeft) // break reference
                });
            }
            if (haveEnoughPurchaseItems) {
                foundPurchaseItemsTarget = true;
                break; // finish to finding more purchases
            }
        }

        if (foundPurchaseItemsTarget) {
            // remove the old item missing estimation
            item.whereInventoryWasTakenNew = filter(item.whereInventoryWasTakenNew || [], inventoryInfo => get(inventoryInfo, 'type') !== 'VENDOR_PURCHASE_ITEM_MISSING_MODULE') || [];

            // say the amount purchased of this items are enough
            item.isPurchasedAmountEnough = true;
        } else {
            // say the amount purchased of this items are not enough
            item.isPurchasedAmountEnough = false;

            if (ContentUtils.isEmptyArray(item.whereInventoryWasTakenNew)) {
                item.whereInventoryWasTakenNew = [];
            }

            // remove the old item missing estimation
            item.whereInventoryWasTakenNew = filter(item.whereInventoryWasTakenNew || [], inventoryInfo => get(inventoryInfo, 'type') !== 'VENDOR_PURCHASE_ITEM_MISSING_MODULE') || [];

            item.whereInventoryWasTakenNew.push({
                type: 'VENDOR_PURCHASE_ITEM_MISSING_MODULE',
                amountItemNeeded: cloneDeep(item.amountNeededLeft), // break reference
                itemIdNeeded: get(item, 'item.id'),
                dateNeeded: taskDate,
                amountSubtracted: '0',
                amountAfter: '0',
                amountPurchased: '0'
            });
        }

        return {
            itemUpdated: item,
            purchaseItemsUpdated: purchaseItems
        };
    },
    
   processAmountSubtractedFromPurchaseItem(purchaseItem, amountNeededLeft) {
        const relativeUnitAmount = get(purchaseItem, 'relativeUnitAmount');
        this.discountAlreadyReceivedItems(purchaseItem);
        
        const amountPurchased = this._getAmountPurchased(purchaseItem, relativeUnitAmount);
        const haveEnoughPurchaseItems = NumberUtils.isGreaterOrEquals(amountPurchased, amountNeededLeft);

        if (haveEnoughPurchaseItems) {
            purchaseItem.amountSubtracted = amountNeededLeft;
            if (NumberUtils.isGreater(relativeUnitAmount, 1)) {
                // enter here means we need to update the amount request with the relative amount again in order
                // to count properly the next time that need to count how many amount requested was taken
                const amountRelativeSubtracted = NumberUtils.divideAndTruncate(purchaseItem.amountSubtracted, relativeUnitAmount, 10);
                const amountRelativeRemain = NumberUtils.divideAndTruncate(amountPurchased, relativeUnitAmount, 10);
                purchaseItem.amountRequestedUpdated = NumberUtils.subtract(amountRelativeRemain, amountRelativeSubtracted);
            } else {
                purchaseItem.amountRequestedUpdated = NumberUtils.subtract(amountPurchased, amountNeededLeft);
            }
        } else {
            purchaseItem.amountSubtracted = amountPurchased;
            purchaseItem.amountRequestedUpdated = '0';
        }

        return {
            purchaseItemUpdated: purchaseItem,
            amountPurchased,
            haveEnoughPurchaseItems
        };
    },
    
    discountAlreadyReceivedItems: function (purchaseItem) {
        if (purchaseItem.state === 'MORE_ON_THE_WAY' && !purchaseItem.isAmountReceivedAlreadyCounted) {
            // Enter here means we have some PO that I already received partially, but I'm waiting to receive more.
            // So, we need to updated in the PO, the amount that we already received in order to don't calculate the amount ordered
            // as the same of initial amount. Eg, As a admin, I already received 200 of 1000 item requested, It's left 800 to count in yellow state not 1000.
            purchaseItem.isAmountReceivedAlreadyCounted = true;
            purchaseItem.amountRequestedUpdated = NumberUtils.subtract(purchaseItem.amountRequestedNumber, purchaseItem.totalAmountReceived);
        }
    },
    
    toSystemUnitAmount(relativeAmount, relativeUnitAmount) {
        return Math.round(NumberUtils.multiplyAndTruncate(relativeAmount, relativeUnitAmount, 4));
    },

    toRelativeAmountPurchased(systemAmount, relativeUnitAmount) {
        return NumberUtils.divideAndTruncate(systemAmount, relativeUnitAmount, 4);
    },

    _getAmountPurchased(purchaseItem, relativeUnitAmount = 1) {
        const amountPurchased = get(purchaseItem, 'amountRequestedUpdated', get(purchaseItem, 'amountRequested'));

        if (NumberUtils.isGreater(relativeUnitAmount, 1)) {
            return this.toSystemUnitAmount(relativeUnitAmount, amountPurchased);
        }

        return amountPurchased;
    },

    /**
     * This flag help to indicate when given product should not be calculated automatically. So imagine the situation
     * where the user indicate manually where he want to subtract their item. (the user indicate which location want, different that the program suggest) Or
     * laso other scenario would be after the user confirm their product.
     * So whenever scenario was, when this flag is turn-on, we should not auto-calculated again about which location the item will taken, until turn-off again this indicator.
     * @param product
     * @returns {*}
     */
    turnOnWhereInventoryWasTakenNonChanged(product) {
        const items = get(product, 'items');

        if (ContentUtils.isNotEmptyArray(items)) {
            product.items = map(product.items, item => {
                item.preventRecalculateWhereInventoryWasTaken = true;
                return item;
            });
        }

        return product;
    },


    /**
     * The opposite of {@link this.turnOnWhereInventoryWasTakenNonChanged()}.
     * @param product
     * @returns {*}
     */
    turnOffWhereInventoryWasTakenNonChanged(product) {
        const items = get(product, 'items');

        if (ContentUtils.isNotEmptyArray(items)) {
            product.items = map(product.items, item => {
                item.preventRecalculateWhereInventoryWasTaken = false;
                return item;
            });
        }

        return product;
    },

    subtractAmountFromLocationItems(itemTarget, locationItemsAvailable) {

        const originalItem = DashboardServiceCache.getItemById(get(itemTarget, 'itemId'));
        const havePreferredLocation = get(originalItem, 'preferredLocation') !== null;
        if (havePreferredLocation) {
            // enter here means will order by preferred location first the items availables
            // in order to consume first
            const preferredLocationName = get(originalItem, 'preferredLocation.name');
            locationItemsAvailable = ContentUtils.sortListByValue(locationItemsAvailable, 'location.name', preferredLocationName);
        }

        // item id which need the subtract from some location
        const itemTargetId = get(itemTarget, 'item.id');

        const locationItemsAvailablesFiltered = locationItemsAvailable.filter(item => item.item.id === itemTargetId);

        let locationItemsAvailableUpdated = map(locationItemsAvailablesFiltered, locationItemAvailable => {
            // ask if still pending subtract the amount of the item
            // or if location items is the need
            const isAlreadyDiscountedAllItemsNeeded = NumberUtils.isEqual(itemTarget.amountNeededLeft, '0');
            const isNotEnoughItemAvailable = NumberUtils.isLessOrEquals(locationItemAvailable.amount, '0');
            const isLocationItemTarget = (get(locationItemAvailable, 'itemId') || get(locationItemAvailable, 'item.id')) === itemTargetId;

            if (isAlreadyDiscountedAllItemsNeeded || !isLocationItemTarget || isNotEnoughItemAvailable) {
                // when we already subtract all the item needed or
                // the location item iterate it does not match with "itemTargetId" or
                // is not enough item available in this location to subtract ---> keep searching for another location
                return locationItemAvailable;
            }


            const amountInStockAvailable = get(locationItemAvailable, 'amount');
            const isEnoughLocationItem = NumberUtils.isLessOrEquals(itemTarget.amountNeededLeft, amountInStockAvailable);
            let amountStockSubtracted = '0';

            if (isEnoughLocationItem) {
                amountStockSubtracted = itemTarget.amountNeededLeft;
                itemTarget.amountNeededLeft = '0';
                locationItemAvailable.amount = NumberUtils.subtract(amountInStockAvailable, amountStockSubtracted);
                // reset old purchased option just in case to clean old estimation
                itemTarget.isPurchased = false;
                itemTarget.isPurchasedAmountEnough = false;
            } else {
                // we discount all the amount from stock
                amountStockSubtracted = amountInStockAvailable;
                itemTarget.amountNeededLeft = NumberUtils.subtract(itemTarget.amountNeededLeft, amountInStockAvailable);
                locationItemAvailable.amount = '0';
            }

            if (ContentUtils.isEmptyArray(itemTarget.whereInventoryWasTakenNew)) {
                itemTarget.whereInventoryWasTakenNew = [];
            }

            // save the operation
            itemTarget.whereInventoryWasTakenNew.push({
                type: 'LOCATION_ITEM_MODULE',
                itemId: get(itemTarget, 'item.id'),
                amountSubtracted: amountStockSubtracted,
                locationItemId: locationItemAvailable.id,
                locationId: get(locationItemAvailable, 'location.id'),
                amountInStock: amountInStockAvailable,
                amountAfter: locationItemAvailable.amount,
                amountItemNeededLeft: itemTarget.amountNeededLeft
            });

            return locationItemAvailable;
        });

        if (ContentUtils.isNotEmptyArray(locationItemsAvailableUpdated)) {
            locationItemsAvailableUpdated.forEach(locationItemUpdated => {
                locationItemsAvailableUpdated = ContentUtils.replaceByAttributes(locationItemsAvailable, locationItemUpdated, 'id');
            });
        }

        return {
            locationItemsAvailable: locationItemsAvailableUpdated,
            itemUpdated: itemTarget
        };
    },
    
    getTotalAmountReservedByItem(task, itemId) {
        if (!task || !itemId) {
            return 0;
        }
        
        const products = get(task, 'products', []);
        const total = {
            amountReserved: 0,
            amountPurchased: 0,
            amountItemNeeded: 0
        };
        (products || []).forEach(product => {
            const items = get(product, 'items');
            
            const totalSubtracted = reduce(items, (total, item) => {
                if (_getItemId(item) !== itemId) {
                    return total;
                }
                const allTaskStockReserved = concat(
                  get(item, 'whereInventoryWasTakenNew'),
                  get(item, 'whereInventoryWasTakenSaved')
                )
                .filter(whereInventoryWasTaken => whereInventoryWasTaken && whereInventoryWasTaken.type === 'LOCATION_ITEM_MODULE');
                
                const amountSubtracted = reduce(allTaskStockReserved,
                  (total, whereInventoryWasTaken) => {
                        if (NumberUtils.isGreater(get(whereInventoryWasTaken, 'amountSubtracted'), 0)) {
                            return NumberUtils.sum(total, get(whereInventoryWasTaken, 'amountSubtracted'));
                        }
                      return total;
                  }, 0);
    
                return NumberUtils.sum(total, amountSubtracted)       ;
            }, 0);
            
            const totalAmountPurchased = reduce(items, (total, item) => {
                if (_getItemId(item) !== itemId) {
                    return total;
                }
                const allRequested = concat(
                  get(item, 'whereInventoryWasTakenNew'),
                  get(item, 'whereInventoryWasTakenSaved'))
                .filter(whereInventoryWasTaken => whereInventoryWasTaken && whereInventoryWasTaken.type === 'VENDOR_PURCHASE_ITEM_MODULE');
    
                const amountPurchased = reduce(allRequested,
                  (total, whereInventoryWasTaken) =>
                    NumberUtils.sum(total, get(whereInventoryWasTaken, 'amountSubtracted')),
                  0);
                
                return NumberUtils.sum(total, amountPurchased);
            }, 0);
            
            const totalItemNeeded = reduce(items, (total, item) => {
                if (_getItemId(item) !== itemId) {
                    return total;
                }
                const allTask = concat(get(item, 'whereInventoryWasTakenNew'), get(item, 'whereInventoryWasTakenSaved'));
                const amountSubtracted = reduce(allTask,
                  (total, whereInventoryWasTaken) =>
                    NumberUtils.sum(total, get(whereInventoryWasTaken, 'amountItemNeeded')),
                  0);
                return NumberUtils.sum(total, amountSubtracted);
                
            }, 0);
            
            total.amountPurchased = NumberUtils.sum(total.amountPurchased, totalAmountPurchased);
            total.amountSubtracted = NumberUtils.sum(total.amountSubtracted, totalSubtracted);
            total.amountItemNeeded = NumberUtils.sum(total.amountItemNeeded, totalItemNeeded);
        });
        
        total.amountPurchased = NumberUtils.formatDecimal(total.amountPurchased);
        total.amountItemNeeded = NumberUtils.formatDecimal(total.amountItemNeeded);
        total.amountSubtracted = NumberUtils.formatDecimal(total.amountSubtracted);
        
        return total;
    },

    subtractAmountFromSpecificLocationItems(itemTarget, locationItemsAvailable) {
        const whereInventoryWasTakenList = get(itemTarget, 'whereInventoryWasTakenNew') || [];

        // update the amount location items with the custom subtraction of given
        // location items availables
        const locationItemsAvailableUpdated = map(locationItemsAvailable, locationItemAvailable => {
            const whereInventoryWasTakenTarget = find(whereInventoryWasTakenList,
                whereInventoryWasTaken => get(whereInventoryWasTaken, 'locationId') === get(locationItemAvailable, 'location.id')
            );

            if (!whereInventoryWasTakenTarget) {
                // enter here means the location items is not affected
                return locationItemAvailable;
            }

            // subtract the specific amount
            const amountInStockAvailable = get(locationItemAvailable, 'amount');
            const amountSubtracted = get(whereInventoryWasTakenTarget, 'amountSubtracted');
            locationItemAvailable.amount = NumberUtils.subtract(amountInStockAvailable, amountSubtracted);

            return locationItemAvailable;
        });

        return {
            locationItemsAvailable: locationItemsAvailableUpdated,
            itemUpdated: itemTarget
        };
    },

    updateProductItemFromProduct(product, newProductItem) {
        if (product && ContentUtils.isNotEmptyArray(product.items)) {
            // update product items which are location amount updated
            product.items = ContentUtils.replaceByAttributes(product.items, newProductItem, 'id');
        }
        return product;
    },

    createWhereInventoryWasTaken(locationId, itemId, productItemAmount, newAmountLocationItem, whereInventoryWasTakenLatest) {
        // we retrieve an existing locationItem in safe way because
        // the amount availability was checked before
        const existingLocationItem = DashboardServiceCache.getLocationItemByLocationIdAndItemId(locationId, itemId);
        const amountSubtractedBefore = this.getTotalAmountSubtractedFromWhereInventoryWasTaken(whereInventoryWasTakenLatest);
        const totalAmountSubtracted = NumberUtils.sum(amountSubtractedBefore, newAmountLocationItem);
        const amountItemNeededLeft = NumberUtils.subtract(productItemAmount, totalAmountSubtracted);

        const existingStockInLocation = get(existingLocationItem, 'amount') || '0.00';
        const amountInStockLeft = NumberUtils.subtract(existingStockInLocation, totalAmountSubtracted);

        return {
            type: 'LOCATION_ITEM_MODULE',
            itemId,
            locationId,
            locationItemId: get(existingLocationItem, 'id'),
            amountSubtracted: newAmountLocationItem,
            amountInStock: existingStockInLocation,
            amountAfter: amountInStockLeft,
            amountItemNeededLeft,
            isNew: true // flag to difference between `saved` to `new` kind of whereInventoryWasTaken once merge list
        };
    },

    getTotalAmountSubtractedFromWhereInventoryWasTaken(whereInventoryWasTaken) {
        const amountsSubtracted = map(whereInventoryWasTaken, inventoryInfo => get(inventoryInfo, 'amountSubtracted'));
        return reduce(amountsSubtracted, (total, amount) => NumberUtils.sum(total, amount));
    },

    /**
     * Subtract the specific location items
     *
     * @param productItem
     * @param {@Number} newAmountLocationItem       The new location which define the user
     * @param locationId
     * @return {{newProductItemAmount: *|string, newWhereInventoryWasTakenItemUpdated: *, productItemUpdated: *}}
     */
    onChangeAmountInLocation(productItem, newAmountLocationItem, locationId) {
        // indicate to the system about non-calculate
        // where inventory was taken automatically until turn-off this flag again
        productItem.preventRecalculateWhereInventoryWasTaken = true;
        const itemId = get(productItem, 'item.id');

        // retrieve the last update of each where inventory was taken
        const whereInventoryWasTakenLatest = this.retrieveLatestWhereInventoryWasTaken(productItem);

        // search if exist some whereInventory was taken from this particular location/item
        let whereInventoryWasTakenItem = find(whereInventoryWasTakenLatest, itemTarget =>
            get(itemTarget, 'locationId') === locationId
            && get(itemTarget, 'itemId') === itemId
            && get(itemTarget, 'type') === 'LOCATION_ITEM_MODULE'
        );
        const productItemAmount = get(productItem, 'amount');

        if (ContentUtils.isNull(whereInventoryWasTakenItem)) {
            // enter here means doesn't exist where location was taken before for this particular location/item
            whereInventoryWasTakenItem = this.createWhereInventoryWasTaken(
                locationId,
                itemId,
                productItemAmount,
                newAmountLocationItem,
                whereInventoryWasTakenLatest
            );
            productItem.whereInventoryWasTakenNew = union(productItem.whereInventoryWasTakenNew, [whereInventoryWasTakenItem]);

        } else {
            // enter here means already exist where location was taken, so we need to change it
            const whereInventoryWasTakenItemUpdated = this.changeAmountWhereInventoryWasTakenLocation(
                whereInventoryWasTakenItem,
                newAmountLocationItem
            );

            const newWhereInventoryWasDeleted = whereInventoryWasTakenItemUpdated === undefined;
            if (newWhereInventoryWasDeleted) {
                // enter here means the user was selected '0' in one particular location
                // so, we create a new one with 0
                const newWhereInventoryWasTakenItem = this.createWhereInventoryWasTaken(
                    locationId,
                    itemId,
                    productItemAmount,
                    newAmountLocationItem,
                    whereInventoryWasTakenLatest
                );
                // remove the before wherePositionWasTaken
                productItem.whereInventoryWasTakenNew = cloneDeep(ContentUtils.removeItemByObjectEquality(productItem.whereInventoryWasTakenNew, whereInventoryWasTakenItem)) || [];
                productItem.whereInventoryWasTakenNew.push(newWhereInventoryWasTakenItem);
            } else {
                // remove the before wherePositionWasTaken
                productItem.whereInventoryWasTakenNew = cloneDeep(ContentUtils.removeItemByObjectEquality(productItem.whereInventoryWasTakenNew, whereInventoryWasTakenItem)) || [];
                productItem.whereInventoryWasTakenNew.push(whereInventoryWasTakenItemUpdated);
            }
        }

        // retrieve each of `whereInventoryWasTaken` after update the productItem
        const whereInventoryWasTakenLatestAfterUpdate = this.retrieveLatestWhereInventoryWasTaken(productItem);
        const newProductItemAmount = this.getTotalAmountSubtracted(whereInventoryWasTakenLatestAfterUpdate) || '0.00'; // empty must be zero

        // change product item to reflect the changes we want.
        productItem.amount = newProductItemAmount;
        productItem.manualAmount = newProductItemAmount;
        return productItem;
    },

    retrieveLatestWhereInventoryWasTaken(productItem) {
        const whereInventoryWasTakenSaved = get(productItem, 'whereInventoryWasTakenSaved') || [];
        const whereInventoryWasTakenNew = get(productItem, 'whereInventoryWasTakenNew') || [];

        const whereInventoryWasTakenSavedFiltered = filter(whereInventoryWasTakenSaved, inventoryInfoSaved => {
            const locationId = get(inventoryInfoSaved, 'locationId');
            const itemId = get(inventoryInfoSaved, 'itemId');
            const existInfoSavedNew = some(whereInventoryWasTakenNew, inventoryInfoNew =>
                get(inventoryInfoNew, 'locationId') === locationId &&
                get(inventoryInfoNew, 'itemId') === itemId);
            return existInfoSavedNew ? false : true;
        });

        return union(whereInventoryWasTakenSavedFiltered, whereInventoryWasTakenNew);
    },

    changeAmountOfProductItem(product, itemId, newProductItemAmount) {
        const productItems = get(product, 'items');
        let productItemTarget = find(productItems, productItem => get(productItem, 'id') === itemId || get(productItem, 'itemId') === itemId);
        if (productItemTarget) {
            productItemTarget.amount = newProductItemAmount;
            productItemTarget.manualAmount = newProductItemAmount;
            product.items = ContentUtils.replaceByAttributes(productItems, productItemTarget, 'id');
            return product;
        } else {
            return product;
        }
    },

    getTotalAmountSubtracted(whereInventoryWasSubtractedList) {
        const amountsSubtracted = map(whereInventoryWasSubtractedList, whereInventoryWasSubtracted => {
            const type = get(whereInventoryWasSubtracted, 'type');
            if (type === 'LOCATION_ITEM_MODULE' || type === 'VENDOR_PURCHASE_ITEM_MODULE') {
                return get(whereInventoryWasSubtracted, 'amountSubtracted');
            }

            if (type === 'VENDOR_PURCHASE_ITEM_MISSING_MODULE') {
                return get(whereInventoryWasSubtracted, 'amountItemNeeded');
            }

            return '0'; //should not entry here
        });
        return reduce(amountsSubtracted, (sum, amount) => NumberUtils.sum(sum, amount));
    },

    changeAmountWhereInventoryWasTakenLocation(whereInventoryWasTakenLocation, newAmount) {
        const hastNotAmountDefined = NumberUtils.isLessOrEquals(newAmount, 0);

        if (hastNotAmountDefined || ContentUtils.isNull(whereInventoryWasTakenLocation)) {
            // enter here means the location was taken should be removed
            return undefined;
        }

        const amountInStock = whereInventoryWasTakenLocation.amountInStock;
        const isNewAmountSubtractedGreaterThanAmountInLocation = NumberUtils.isGreater(newAmount, amountInStock);

        if (isNewAmountSubtractedGreaterThanAmountInLocation) {
            // enter here means is not possible to subtract
            // more than available in location
            return whereInventoryWasTakenLocation;
        }

        return merge(whereInventoryWasTakenLocation, {
            amountInStock: amountInStock,
            amountAfter: NumberUtils.subtract(amountInStock, newAmount),
            amountSubtracted: newAmount,
            amountItemNeededLeft: NumberUtils.subtract(whereInventoryWasTakenLocation.amountItemNeededLeft, newAmount)
        });
    },

    getProposalLocationItems(locations, items, locationItems) {
        let proposalLocationItems = [];

        let index = 0;
        for (const location of locations) {
            for (const item of items) {

                const locationItemTarget = find(locationItems, locationItem =>
                    (get(locationItem, 'item.id') || get(locationItem, 'itemId')) === get(item, 'id') &&
                    (get(locationItem, 'location.id') || get(locationItem, 'locationId')) === get(location, 'id')
                );
                const amountInLocation = get(locationItemTarget, 'amount') || 0;
                const isAlreadyCreated = ContentUtils.isNotNullOrUndefined(locationItemTarget);

                if (isAlreadyCreated && NumberUtils.isGreater(NumberUtils.getNumber(amountInLocation), 0)) {
                    // not need to create a proposal item
                    proposalLocationItems.push(locationItemTarget);
                    continue;
                }

                const isCurrentLocationPreferred = get(item, 'preferredLocation.id') === get(location, 'id');
                if (!isCurrentLocationPreferred) {
                    // if the item is not preferred location, it doesn't make sense to create an item proposal,
                    // only for preferred location item exist proposal item
                    continue;
                }

                index++;

                // create a new empty proposal location items
                proposalLocationItems.push({
                    id: index, // need a fake id, in order to work table selection properly
                    isProposalItem: true,
                    item,
                    location,
                    amount: '0'
                });
            }
        }

        return proposalLocationItems;
    },

    _getItemAndItemId(itemNonDiscountedYet) {
        const isProductItem = get(itemNonDiscountedYet, 'item') !== undefined;
        let item = itemNonDiscountedYet;
        let itemId;
        if (isProductItem) {
            // first check if the manual amount was defined, then
            // check if have some estimateAmount, then a amount and by
            // latest the defaultAmount.
            item.amount = itemNonDiscountedYet.manualAmount || itemNonDiscountedYet.estimateAmount || itemNonDiscountedYet.amount || itemNonDiscountedYet.defaultAmount;
            itemId = get(item, 'item.id');
            item.itemId = itemId;
        } else {
            itemId = get(item, 'itemId') || get(item, 'id');
            item.itemId = itemId;
        }

        return {
            item,
            itemId
        };
    },

    sortPurchaseItemByAmountRequested(purchasedItems) {
        if (ContentUtils.isEmptyArray(purchasedItems)) {
            return purchasedItems;
        }

        const amountPurchaseWithNumber = map(purchasedItems, purchaseItem => {
            purchaseItem.amountRequestedNumber = +purchaseItem.amountRequested;
            return purchaseItem;
        });
        const purchaseItemsSorted = orderBy(amountPurchaseWithNumber, ['amountRequestedNumber'], ['desc']);
        return purchaseItemsSorted;
    },

    onChangeProducts(ordersCloned, nextState, productWithoutChange, newAmount, uniqueId, task, avoidMultiplyItemByAmountProduct, endDateOrder) {
        const draftOrder = nextState.order;
    
        // This is important to don't re-calculate in case of exist a whereinventory was taken
        // which indicate the user make some changes from the modal and don't want to remove these changes.
        const hasSomeWhereInventoryWasTaken = some(productWithoutChange.items, item => item && item.whereInventoryWasTakenNew && item.whereInventoryWasTakenNew.length > 0);
        
        if (productWithoutChange.isNeededCalculateInventory || hasSomeWhereInventoryWasTaken) {
            // enter here means we turn-off the flag about
            // the needed to calculate the inventory, this turn-on from `InventoryStore.hydrateTaskWithProductsAmount`
            productWithoutChange.isNeededCalculateInventory = false;
        }

        const isUnconfirmedProductAlreadySaved = productWithoutChange.isConfirmed === false && productWithoutChange.isDiscounted;
        if (isUnconfirmedProductAlreadySaved && !productWithoutChange.needToPublishDisconfirmation) {
            productWithoutChange = InventoryStore.toggleConfirmProduct(productWithoutChange, task.date);
        }

        // update the product from the task
        let taskWithProductUpdated = DashboardModalUtils.updateProductOfTask(task, productWithoutChange);

        // add/update newAmount of product in task
        const taskFieldName = InventoryStore.getTaskFieldName(taskWithProductUpdated);
        const orderTasks = get(draftOrder, taskFieldName);
        const orderTasksUpdated = DashboardModalUtils.updateTask(orderTasks, taskWithProductUpdated);

        // update task with new product amount
        draftOrder[taskFieldName] = orderTasksUpdated;

        // update orders with the latest draft order
        ordersCloned = OrderUtils.updateOrder(ordersCloned, draftOrder);
    
        // include dashboard orders in order to calculate scope inventory properly
        ordersCloned = hydrateWithDashboardOrders(draftOrder, ordersCloned);
        
        const startDate = OrderUtils.getStartDateOrder(draftOrder);
        if (DateUtils.isBefore(startDate, SessionStore.getMaxScopeInventoryDate())) {
            ordersCloned = InventoryStore.hydrateOrderTaskWithInventoryScopeEstimation(ordersCloned, startDate, endDateOrder);
        } else {
            // hydrate the products and items which the state of inventory status
            ordersCloned = InventoryStore.hydrateOrderTaskWithInventoryScopeEstimation(ordersCloned, SessionStore.getMaxScopeInventoryDate(), endDateOrder);
        }

        return {
            [taskFieldName]: filter(orderTasksUpdated, task => task), // remove undefined positions
            order: draftOrder,
            ordersCloned,
            taskWithProductUpdated
        };
    },

    onChangeSingleProducts(ordersCloned, nextState, productWithoutChange, newAmount, uniqueId, task, avoidMultiplyItemByAmountProduct, endDateOrder) {
        let taskWithProductUpdated;

        // get the current draft order (new / edit mode)
        const draftOrder = nextState.order;

        const isUnconfirmedProductAlreadySaved = productWithoutChange.isConfirmed === false && productWithoutChange.isDiscounted;
        if (isUnconfirmedProductAlreadySaved && !productWithoutChange.needToPublishDisconfirmation) {
            productWithoutChange = InventoryStore.toggleConfirmProduct(productWithoutChange, task.date);
        }

        // update the product from the task
        taskWithProductUpdated = DashboardModalUtils.updateProductOfTask(task, productWithoutChange);

        // add/update newAmount of product in task
        const taskFieldName = InventoryStore.getTaskFieldName(taskWithProductUpdated, true);
        const orderTasks = get(draftOrder, taskFieldName);
        const orderTasksUpdated = DashboardModalUtils.updateTask(orderTasks, taskWithProductUpdated);

        // update task with new product amount
        draftOrder[taskFieldName] = orderTasksUpdated;
        draftOrder.isSingle = true; // always override this mark to be sure

        // update orders with the latest draft order
        ordersCloned = OrderUtils.updateOrder(ordersCloned, draftOrder);
        
        // include dashboard orders in order to calculate scope inventory properly
        ordersCloned = hydrateWithDashboardOrders(draftOrder, ordersCloned);
    
        // hydrate the products and items which the state of inventory status
        const startDate = OrderUtils.getStartDateOrder(draftOrder);
        if (DateUtils.isBefore(startDate, SessionStore.getMaxScopeInventoryDate())) {
            ordersCloned = InventoryStore.hydrateOrderTaskWithInventoryScopeEstimation(ordersCloned, startDate, endDateOrder);
        } else {
            ordersCloned = InventoryStore.hydrateOrderTaskWithInventoryScopeEstimation(ordersCloned, SessionStore.getMaxScopeInventoryDate(), endDateOrder);
        }

        return {
            [taskFieldName]: filter(orderTasksUpdated, task => task), // remove undefined positions
            order: draftOrder,
            ordersCloned,
            taskWithProductUpdated
        };
    },

    /**
     * Search given task a specific item.
     * @param task
     * @param item
     * @returns {*}
     */
    someItemsExistInGivenTask(task, item) {
        if (!item) {
            return false;
        }
        const products = get(task, 'products') || [];
        return some(products, product => {
            const items = get(product, 'items');
            return some(items, itemSearch => _getItemId(itemSearch) === _getItemId(item));
        });
    },

    /**
     * Search given tasks a specific item.
     * @param task
     * @param item
     * @returns {*}
     */
    isSomeItemInTasks(tasks, item) {
        return some(tasks, task => this.someItemsExistInGivenTask(task, item));
    },

    onChangeProductByAmount(product, newAmount) {
        if (ContentUtils.isNotNullOrUndefined(newAmount)) {
            // when enter the first time never will have defaultAmount, so we
            const productOrigin = cloneDeep(DashboardServiceCache.getProductById(get(product, 'id')));
            const defaultProductVolume = get(productOrigin, 'volume');

            product.volume = NumberUtils.multiplyAndTruncate(newAmount, defaultProductVolume);
            product.amount = NumberUtils.truncate(newAmount, 2);
            // reset custom amount already assigned from small amount
            product.customAmount = undefined;
            product.customVolume = undefined;

            // update the amount of each item
            const items = get(product, 'items');
            const itemsUpdated = _multiplyAmountOfItems(items, product.amount);
            product.items = itemsUpdated;
        }
        return product;
    },

    onChangeProductByVolume(product, newVolume) {
        if (ContentUtils.isNotNullOrUndefined(newVolume)) {
            const productOrigin = cloneDeep(DashboardServiceCache.getProductById(get(product, 'id')));
            const defaultProductVolume = get(productOrigin, 'volume');
            product.amount = NumberUtils.divide(newVolume, defaultProductVolume);
            product.volume = NumberUtils.truncate(newVolume, 2);
            // reset custom amount already assigned from small amount
            product.customAmount = undefined;
            product.customVolume = undefined;

            // update the amount of each item
            const items = get(product, 'items');
            const itemsUpdated = _multiplyAmountOfItems(items, product.amount);
            product.items = itemsUpdated;
        }
        return product;
    },

    onChangeAmountItem(product, item, itemAmount) {
        if (product === undefined || item === undefined || itemAmount === undefined) {
            return product;
        }

        const isAlreadySubtracted = item && item.whereInventoryWasTakenSave;
        let currentProductItem;
        const productItems = get(product, 'items');
        currentProductItem = find(productItems, productItem => get(productItem, 'itemId') === get(item, 'itemId'));

        if (isAlreadySubtracted) {
            currentProductItem = set(currentProductItem, 'adjustAmount', ContentUtils.getNumber(itemAmount, 0));
        } else {
            currentProductItem = set(currentProductItem, 'manualAmount', ContentUtils.getNumber(itemAmount, 0));
        }

        return product;
    },

    /**
     * Mark a product as a Confirmed.
     * @param product
     * @return {*}
     */
    toggleConfirmProduct(product, dateTask) {
        if (product && !DateUtils.isAfterToDayFullDate(product.endDate || dateTask)) {
            product.isConfirmed = !product.isConfirmed;
            if (!product.isConfirmed && product.isDiscounted) {
                product.needToPublishDisconfirmation = true;
            } else {
                product.needToPublishDisconfirmation = false;
            }
        }

        return product;
    },

    haveAProducts(task) {
        const haveProducts = task && task.products && task.products.length > 0;
        return haveProducts;
    },

    /**
     * Indicate if some product have all theirs items already discounted
     * @param task
     * @return {boolean}
     */
    isSomeProductAlreadyDiscounted(task) {
        const haveProducts = task && task.products && task.products.length > 0;
        if (haveProducts) {
            return some(task.products, product =>
                every(ContentUtils.filterEnable(product.items), item => get(item, 'whereInventoryWasTakenSave'))
            );
        }
        return false;
    },

    existSomePendingToPublishItem(task) {
        const haveProducts = task && task.products && task.products.length > 0;
        if (haveProducts) {
            return this.isEveryProductAlreadyDiscounted(task) && some(task.products,
                product =>
                    some(ContentUtils.filterEnable(product.items), item => get(item, 'whereInventoryWasTakenNew'))
            );
        }
        return false;
    },

    isEveryProductAlreadyDiscounted(task) {
        const haveProducts = task && task.products && task.products.length > 0;
        if (haveProducts) {
            return every(task.products, product => every(ContentUtils.filterEnable(product.items), item => item.whereInventoryWasTakenSave));
        }
        return false;
    },


    getTaskFieldName(task, isSingle) {
        return _getTaskFieldName(task, isSingle);
    },

    addDefaultAmountAndVolumeToProduct(product) {
        const isNotDefinedAmount = ContentUtils.isNull(get(product, 'amount'));

        if (isNotDefinedAmount) {
            return product;
        }

        const defaultAmount = get(product, 'defaultAmount');
        const defaultVolume = get(product, 'defaultVolume');
        const isNotDefinedDefaultAmount = ContentUtils.isNull(defaultAmount);
        const isNotDefinedDefaultVolume = ContentUtils.isNull(defaultVolume);

        if (isNotDefinedDefaultAmount) {
            product.defaultAmount = get(product, 'amount');
            Object.defineProperty(product, 'defaultAmount', {writable: false, configurable: false, enumerable: true});
        }

        if (isNotDefinedDefaultVolume) {
            const originalProduct = DashboardServiceCache.getProductById(get(product, 'id'));
            product.defaultVolume = get(originalProduct, 'volume');
            Object.defineProperty(product, 'defaultVolume', {writable: false, configurable: false, enumerable: true});
        }

        return product;
    },

    hydrateWithDefaultAmountItems(tasks) {
        return map(tasks, task => {
            const products = get(task, 'products') || [];

            task.products = map(products, product => {
                const items = get(product, 'items') || [];

                product.items = map(items, item => {
                    if (ContentUtils.isNull(item.defaultAmount)) {
                        const itemId = get(item, 'itemId');
                        const productId = get(product, 'id');

                        const originItem = DashboardServiceCache.getProductItemsByProductIdAndItemId(productId, itemId);
                        if (originItem) {
                            // set default item
                            item = Object.defineProperty(item, 'defaultAmount', {
                                writable: false,
                                enumerable: true,
                                configurable: false,
                                value: get(originItem, 'defaultAmount')
                            });
                        }
                    }
                    return item;
                });
                return product;
            });
            return task;
        });
    },

    addDefaultAmountOfProductItem(item, productId) {
        const isNotDefinedProductId = ContentUtils.isNull(productId);
        if (isNotDefinedProductId) {
            return item;
        }

        const itemId = get(item, 'item.id') || get(item, 'itemId');

        const productItemTarget = DashboardServiceCache.getProductItemsByProductIdAndItemId(productId, itemId);
        if (ContentUtils.isNotNullOrUndefined(productItemTarget)) {
            return Object.defineProperty(item, 'defaultAmount', {
                writable: false,
                configurable: false,
                enumerable: true,
                value: productItemTarget.defaultAmount
            });
        }

        return item;
    },

    haveMissingItemWhereLocationWasSubtract(products) {
        const subtractionProducts = filter(products, productFilter => productFilter.addition);
        return some(subtractionProducts, product => {
            let list = [];

            const whereInventoryWasTakenSave = get(product, 'whereInventoryWasTakenSave');
            if (ContentUtils.isNotEmptyArray(whereInventoryWasTakenSave)) {
                list = union(list, whereInventoryWasTakenSave);
            }

            const whereInventoryWasTakenNew = get(product, 'whereInventoryWasTakenNew');
            if (ContentUtils.isNotEmptyArray(whereInventoryWasTakenNew)) {
                list = union(list, whereInventoryWasTakenNew);
            }

            return some(list, item => get(item, 'type') !== 'LOCATION_ITEM_MODULE');
        });
    },

    onConfirmProduct(prevState, product, task, ordersCloned, temporaryOrderId) {
        let draftOrder;
        // get the current draft order (new / edit mode)

        if (prevState.order) {
            draftOrder = prevState.order;
        } else {
            const currentDraftOrder = find(ordersCloned, order => get(order, 'temporaryId') === temporaryOrderId);
            const existOrderDraft = currentDraftOrder !== undefined;
            if (existOrderDraft) {
                draftOrder = currentDraftOrder;
            }
        }

        let taskFieldName,
            productUpdated,
            orderTasksUpdated;

        const dateTask = get(task, 'date');
        productUpdated = InventoryStore.toggleConfirmProduct(product, dateTask);
        const taskWithProductUpdated = DashboardModalUtils.updateProductOfTask(task, productUpdated);
        taskFieldName = InventoryStore.getTaskFieldName(taskWithProductUpdated, prevState.order.isSingle);
        const orderTasks = get(draftOrder, taskFieldName);
        
        orderTasksUpdated = DashboardModalUtils.updateTask(orderTasks, taskWithProductUpdated);
        draftOrder[taskFieldName] = orderTasksUpdated;
        ordersCloned = ContentUtils.replaceByAttributes(ordersCloned, draftOrder, 'id');
    
        if (productUpdated.isConfirmed) {
            productUpdated = this.turnOnWhereInventoryWasTakenNonChanged(productUpdated);
        } else {
            productUpdated = this.turnOffWhereInventoryWasTakenNonChanged(productUpdated);
            // We need to clear all where inventory was taken because we are changing
            // the parent amount, to need to re-calculate inventory again.
            productUpdated = OrderUtils.cleanAllWhereInventoryWasTakenByProduct(productUpdated);
            // The following needs to be `undefined`, are related with InventoryStore.onChangeProducts and InventoryStore._hydrateOrderTaskWithInventoryScopeEstimationByTask
            productUpdated.isNeededCalculateInventory = undefined;
        }

        return {
            product: productUpdated,
            [taskFieldName]: filter(orderTasksUpdated, task => task), // remove undefined positions
            ordersCloned
        };
    },

    hydrateProductWithDefaultAmountAndVolume(existingTask) {
        const products = get(existingTask, 'products');

        if (ContentUtils.isNotEmptyArray(products)) {
            let productsUpdated = [];
            const fullProducts = DashboardServiceCache.getProducts();

            for (const product of products) {
                const productTarget = find(fullProducts, fullProduct => get(fullProduct, 'id') === get(product, 'id'));

                // complete each product task with full product info
                let productUpdated = merge(productTarget, product);
                let productUpdatedWithItems = _hydrateItems(productUpdated);
                productUpdated = this.addDefaultAmountAndVolumeToProduct(productUpdatedWithItems);

                productUpdated.taskDate = existingTask.date;

                // set immutable `defaultVolume` field
                if (ContentUtils.isNull(productUpdatedWithItems.defaultVolume)) {
                    Object.defineProperty(productUpdatedWithItems, 'defaultVolume', {
                        enumerable: true,
                        writable: false,
                        configurable: false
                    });
                }
                productsUpdated.push(productUpdatedWithItems);
            }

            // update full products
            existingTask.products = productsUpdated;
        }

        return existingTask;
    },

    markProductsAsNeededToBeCalculateFromInventory(products, isNeeded = true) {
        return map(products, product => {
            product.isNeededCalculateInventory = isNeeded;
            return product;
        });
    }
    
};

function _hydrateItems(productUpdated) {
    const itemsDry = get(productUpdated, 'items');
    const productId = get(productUpdated, 'id');

    if (ContentUtils.isNotEmptyArray(itemsDry)) {
        const itemsfull = DashboardServiceCache.getItems();
        let itemsUpdated = [];
        for (const itemDry of itemsDry) {
            const itemTarget = find(itemsfull, item => get(item, 'id') === (get(itemDry, 'itemId') || get(itemDry, 'item.id')));
            let itemUpdate = merge(itemDry, itemTarget, {defaultAmount: get(itemDry, 'amount')});
            // make a `defaultAmount` field immutable
            itemUpdate = InventoryStore.addDefaultAmountOfProductItem(itemUpdate, productId);
            itemsUpdated.push(itemUpdate);
        }
        productUpdated.items = itemsUpdated;
    }
    return productUpdated;
}

function _multiplyAmountOfItems(items, newAmount) {
    return map(items, item => {
        const itemAmount = get(item, 'defaultAmount', 1);
        item.estimateAmount = NumberUtils.multiply(itemAmount, newAmount);
        item.manualAmount = undefined;
        return item;
    });
}

function _sortItemsFromProductsAndFilterDisabled(tasks) {
    if (Array.isArray(tasks)) {
        return map(tasks, task => {
            return _sortItemsFromProductAndFilterDisabled(task);
        });
    } else {
        return _sortItemsFromProductAndFilterDisabled(tasks);
    }
}

function _sortItemsFromProductAndFilterDisabled(task) {
    if (task !== undefined) {

        const products = get(task, 'products');
        if (ContentUtils.isEmptyArray(products)) {
            return task;
        }

        const productsUpdated = map(products, product => {
            const items = ContentUtils.filterEnable(get(product, 'items'));

            if (ContentUtils.isEmptyArray(items)) {
                return product;
            }
            const itemsSorted = sortBy(items, 'item.description');
            return set(product, 'items', itemsSorted);
        });

        task.products = productsUpdated;
        return task;
    }
}

function _hydrateTaskWithProducts(task, beerStyleId, filteredProducts, filteredProductIcons, filteredProductItems) {
    const productByIcons = DashboardServiceCache.getProductByIconNameEnables(get(task, 'name'));
    const productsNeeded = productByIcons.filter(product => String(product.beerStyle.id) === String(beerStyleId));
    
    if (ContentUtils.isEmptyArray(productsNeeded)) {
        return task;
    }
    
    const existingProducts = get(task, 'products', []).filter(p => NumberUtils.isGreater(p.amount, 0));
    const allProductsWithExistingProducts = _keepExistingProduct(productsNeeded, existingProducts);
    let safeProducts = cloneDeep(allProductsWithExistingProducts); // clone products to prevent unpredictable product changes

    // filter only the products which will be used for the specific beer style & icon defined
    //let electedProducts = _filterSuitableProductsForTask(safeProducts, beerStyleId, task, filteredProducts, filteredProductIcons);

   if (ContentUtils.isNotEmptyArray(safeProducts)) {
        safeProducts = _hydrateProducts(safeProducts, filteredProducts);
        safeProducts = _hydrateProductItems(safeProducts, filteredProductItems);
    }

    if (task) {
        task.products = safeProducts || [];
        task.products = ContentUtils.removeDuplicateByAttribute(task.products, ['id']);
    }

    return task;
}

function _keepExistingProduct(allNewProducts, existingProduct) {
    const existingProductsId = map(existingProduct, product => product.id);
    const newProductWithoutExistingProduct = filter(allNewProducts, product => !existingProductsId.includes(product.id));
    return ContentUtils.filterEnable(union(newProductWithoutExistingProduct, existingProduct));
}


/**
 * The idea behind hydration product, like other hydration's, is to avoid persist duplicate information from server.
 * In order to archive that we need to fill with full information of each product each on dry product.
 * @param dryProducts
 * @return {*}
 * @private
 */
function _hydrateProducts(dryProducts, fullProducts = DashboardServiceCache.getProducts()) {
    return map(dryProducts, dryProduct => {
        const defaultProduct = find(fullProducts, defaultProduct => get(defaultProduct, 'id') === get(dryProduct, 'id'));
        // make shallow hydration are enough
        const defaultVolume = get(defaultProduct, 'volume');
        const productAmount = get(dryProduct, 'amount');

        // hydrate volume multiplied
        if (NumberUtils.isGreater(defaultVolume, '0')) {
            dryProduct.volume = NumberUtils.multiply(defaultVolume, productAmount);
        } else {
            dryProduct.volume = '0';
        }
        return assign(defaultProduct, dryProduct);
    });
}


function _hydrateProductItems(products, fullProductItems = DashboardServiceCache.getProductItems()) {
    const safeProducts = cloneDeep(products);
    return map(safeProducts, product => {
        if (ContentUtils.isEmptyArray(product.items)) {
            // Enter here when recently fetched the products from server and don't have item yet assigned,
            // so we need to complete the default items configuration for each products.
            product.items = cloneDeep(filter(fullProductItems, productItem => get(productItem, 'product.id') === get(product, 'id')));
            product.items = hydrateItems(product.items);
        } else {
            // Enter here when:
            // A) The product is recently fetched from server, which have items but
            // need to hydrate with full item information.
            // B) The product have items, but need to be updated for new user changes.
            product.items = map(product.items, item => _hydrateProductItem(item));
        }
        return product;
    });
}

function _hydrateProductItem(dryProductItem) {
    if (dryProductItem) {
        const productItemTarget = cloneDeep(find(
            DashboardServiceCache.getProductItems(), productItem => get(productItem, 'item.id') === (get(dryProductItem, 'itemId') || get(dryProductItem, 'item.id'))
        ));

        // hydrate with full items info
        const items = get(productItemTarget, 'items');
        const haveItems = ContentUtils.isNotEmptyArray(items);
        if (haveItems) {
            const itemsUpdated = hydrateItems(items);
            productItemTarget.items = itemsUpdated;
        }

        // always override default productItem (which came from server)
        // to product item from UI (which have the latest user changes)
        return merge(productItemTarget, dryProductItem);
    }
    return dryProductItem;
}

function hydrateItems(items) {
    const fullItems = DashboardServiceCache.getItems();
    return map(items, item => {
        item = hydrateItem(item, fullItems);
        return item;
    });
}

function hydrateItem(item, fullItems = DashboardServiceCache.getItems()) {
    const isProductItem = get(item, 'item.id') !== null;
    let itemTarget;
    if (isProductItem) {
        itemTarget = find(fullItems, fullItem => get(fullItem, 'id') === get(item, 'item.id'));
        itemTarget = merge(itemTarget, get(item, 'item'));
        item.item = merge(itemTarget, item.item);
    } else {
        itemTarget = find(fullItems, fullItem => get(fullItem, 'id') === get(item, 'id'));
        item = merge(itemTarget, item.item);
    }
    return item;
}

function _removeDuplicateTaskWithoutProduct(existingTasks) {
    let clonedExistingTask = cloneDeep(existingTasks);

    // check if exist some duplicate task by date
    const conflictTask = filter(values(groupBy(clonedExistingTask, 'date')), task => task.length > 1);
    const existSomeDuplicatedTaskByDate = conflictTask && flatten(conflictTask).length > 1;

    if (existSomeDuplicatedTaskByDate) {
        //  filter non duplicated task
        const properlyTask = filter(values(groupBy(clonedExistingTask, 'date')), task => task.length === 1);

        // filter from duplicated task, each task which have at least one product
        const taskWhichProducts = filter(flatten(conflictTask), task => ContentUtils.isNotEmptyArray(get(task, 'products')));

        return flatten(union(properlyTask, taskWhichProducts));
    } else {

        return existingTasks;
    }
}

function _calculateTotalVolumeOfProduct(tasks) {
    // filter duplicated task
    const filteredTask = _removeDuplicateTaskWithoutProduct(tasks);

    // clean undefined products and product
    const productsRaw = flatten(map(filteredTask, task => task && task.products));

    // without amount which are just suggested product (not defined by the user)
    const products = filter(flatten(productsRaw), product => product && product.amount);

    // in case of edit order, the product don't have category so we need to add it
    const productsHydrated = _hydrateProducts(products);

    // filter only the product which consume volume
    const productFiltered = filter(productsHydrated, product => includes(Constants.PRODUCT_CATEGORY_CONSUME_VOLUME, product.category));

    // multiply the amount of product by each volume in order to know the total volume by product defined
    // NOTE: use `defaultVolume` just in case that user change volume of product manually, se we need to calculate the orginal one.
    // In case of `amount`, that field was updated by the amount computed which should need calculate the next volume.
    const productsVolume = map(productFiltered, product => NumberUtils.multiply(product.defaultVolume || product.volume, get(product, 'amount', 1)));

    return reduce(productsVolume, (totalVolume, volume) => NumberUtils.sum(totalVolume, volume)) || 0;
}

function _getTaskFieldName(task, isSingle) {
    const taskType = get(task, 'sectionType');
    const isBrewingSection = task && task.turn;
    if (isSingle) {
        if (isBrewingSection) {
            return 'batchs';
        } else {
            return 'singleTasks';
        }
    } else {
        if (isBrewingSection) {
            return 'batchs';
        } else {
            const tasksFieldName = get(ContentUtils.getTankTypeConfiguration(taskType), 'tasksFieldName');
            return tasksFieldName;
        }
    }
}

/**
 * Calculate when the item arrive by given task and purchase item.
 * @param task
 * @param purchaseItem
 * @return {*|Boolean}
 * @private
 */
function _isPurchaseItemArrive(taskDateFromTask, purchaseItem) {
    const taskDate = DateUtils.toDateText(taskDateFromTask);
    const requestedDate = DateUtils.toDateText(purchaseItem.dateRequested);
    const arriveDate = DateUtils.toDateText(DateUtils.addDays(requestedDate, purchaseItem.arrivalDays));
    return DateUtils.isBeforeOrEquals(arriveDate, taskDate);
}

function _resetItemsEstimation(product) {
    const items = get(product, 'items');
    const haveItems = ContentUtils.isNotEmptyArray(items);
    if (haveItems) {
        product.items = map(items, item => {
            item.amountNeededLeft = undefined;
            item.existItemInStock = undefined;
            item.isEnoughInventory = undefined;
            item.amount = undefined;
            item.isPurchased = undefined;
            item.isPurchasedAmountEnough = undefined;
            item.whereInventoryWasTakenNew = undefined;
            item.manualAmount = undefined;
            item.estimateAmount = undefined;
            return item;
        });
    }
    return product;
}

function _getItemId(obj) {
    return get(obj, 'itemId') || get(obj, 'item.id') || get(obj, 'id');
}

function hydrateWithDashboardOrders(draftOrder = {}, ordersCloned = []) {
    const ordersFiltered = draftOrder.id === undefined ? ordersCloned : ordersCloned.filter(order => order.id !== draftOrder.id);
    ordersFiltered.push(draftOrder);
    return ordersFiltered;
}

module.exports = InventoryStore;



/** WEBPACK FOOTER **
 ** ./src/stores/InventoryStore.js
 **/